import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Button, Alert,
  Divider, CircularProgress, Tooltip, Stack, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Avatar, InputAdornment, Menu,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PrintIcon from '@mui/icons-material/Print';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SaveIcon from '@mui/icons-material/Save';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import JobHoverCard from '../components/JobHoverCard';
import { CollapseDivider, COLLAPSE_THUMB_WIDTH } from '../components/CollapseDivider';
import { DocumentPreviewRail } from '../components/DocumentPreviewRail';
import { AiImportBanner } from '../components/AiImportBanner';
import { ResolveCodeDialog } from '../components/ResolveCodeDialog';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { useParams, useNavigate } from 'react-router-dom';
import type { BillLineItem, POLineDetail, Product, PurchaseOrder, Project } from '../data/types';
import { useData } from '../context/DataContext';
import {
  autoMatchInternalCode, autoMatchOrCreate, classifyImportLine, guessLineType,
} from '../utils/codeMatching';
import { ImportResolutionBadge, InfoableButton, type ButtonInfo } from '../components/ImportResolutionBadge';
import { BillEditForm } from '../components/BillEditForm';
import { BillReconciliationTable, type ReconRow } from '../components/BillReconciliationTable';
import { BillReconcileSideBySide } from '../components/BillReconcileSideBySide';
import { buildReconcileBands } from '../utils/reconcileDifferenceView';
import { consumePendingBillImportFile } from '../utils/billImportHandoff';
import { buildAccountingExport } from '../utils/accountingExport';
import { RECON_TABLE_INFO } from '../components/billReconcileTableInfo';
import { SupplierImportPreviewDialog } from '../components/SupplierImportPreviewDialog';
import {
  applyImportPreviewAction, batchImportPreviewAction, buildInitialImportResolutions,
  buildImportLineResolution, countPreviewNewLines,
  resolveImportPreviewInternal, type ImportPreviewDeps,
} from '../utils/importPreviewWorkflow';
import type { ImportLineState, ImportUserAction } from '../utils/codeMatching';
import { BillReconcileStepStrip } from '../components/BillReconcileStepStrip';
import { resolveReconcileStep, reconcileNextAction } from '../utils/billReconcileFlow';
import {
  BTN_STATUS_GREEN, BTN_STANDARD, BTN_GHOST_DOC, BTN_GHOST_DANGER,
  PO_STICKY_LIFECYCLE_SX, PO_STICKY_LIFECYCLE_PUSH, PO_STICKY_LIFECYCLE_TRAIL,
} from '../theme/poButtonTokens';
import type { BillStatus } from '../data/types';
import { BILL_STATUSES } from '../data/types';
import { DEFAULT_BILL_LINE_ITEMS } from '../data/seedData';
import { BRAND, BRAND_HOVER, BRAND_LIGHT, BRAND_BORDER, STATUS_TONE, getStatusTone } from '../theme/tokens';
import { Sparkle } from '../components/Sparkle';
import { StatusChip } from '../components/StatusChip';

const FI_ORANGE = '#F57C00';
const FI_BLUE = '#027BFF';
const BILL_RECON_MAX_WIDTH = 1280;
const BILL_MASTHEAD_EDIT_INSET = 2;

const RECON_FOOTER_INFO: Record<
  | 'preview' | 'close' | 'pdf' | 'reload' | 'exportXero' | 'documentView' | 'sync' | 'import' | 'approveSend' | 'resend'
  | 'approve' | 'markPaid' | 'deleteBill',
  ButtonInfo
> = {
  preview: { title: 'Preview', description: 'Read-only bill view.', example: 'Preview before export.' },
  close: { title: 'Close', description: 'Return to Bills list.', example: 'Close after reconciling.' },
  pdf: { title: 'PDF', description: 'Print or save as PDF.', example: 'PDF for filing.' },
  reload: { title: 'Reload invoice', description: 'Re-upload and re-parse the supplier invoice.', example: 'Reload if the wrong file was attached.' },
  exportXero: { title: 'Export to Xero', description: 'Push approved bill to Xero.', example: 'Export after approval.' },
  documentView: { title: 'Document view', description: 'Switch to the standard bill document layout (line table, no side-by-side matcher).', example: 'Open document view after reconcile is done.' },
  sync: { title: 'Sync to PO', description: 'Deprecated — receipt via Receive all on the matcher.', example: '—' },
  import: {
    title: 'Import supplier invoice',
    description: 'Drop a PDF or image here — Alfred extracts line items and attaches the file for reconciliation.',
    example: 'Drop Quickensted invoice 2346 onto BILL-2026-020 to match against PO-1649.',
  },
  approve: {
    title: 'Approve Bill',
    description: 'Mark this bill approved for payment. Primary action after reconcile — use Receive all on the matcher for receipts.',
    example: 'Approve BILL-2026-021 once unit prices and lines match the PO.',
  },
  approveSend: {
    title: 'Sync Bill to Accounting',
    description: 'Push this bill to your accounting product (Xero / MYOB / QuickBooks) as a payable. If the bill is still Draft, it is approved as part of the sync.',
    example: 'Sync Bill to Xero after lines match the PO.',
  },
  resend: { title: 'Re-send to accounting', description: 'Push this bill to your accounting product again (e.g. after an edit).', example: 'Re-send to Xero after correcting a code.' },
  markPaid: { title: 'Mark as paid', description: 'Close the bill after payment.', example: 'Mark paid when remittance clears.' },
  deleteBill: { title: 'Delete', description: 'Remove draft bill.', example: 'Delete duplicate draft.' },
};

const DISCLOSURE_HEADER_SX = {
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75,
  mx: -0.5, px: 0.75, py: 0.5, borderRadius: 1, cursor: 'pointer',
  '&:hover': { backgroundColor: '#faf8ff' },
} as const;

function fmtDate(d?: string): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return d; }
}

const fmt = (n: number) => `$${n.toFixed(2)}`;

const PROCESSING_STEPS = [
  'Uploading invoice to Alfred…',
  'Reading document structure…',
  'Extracting line items…',
  'Detecting GST treatment…',
  'Complete.',
];

const DEMO_LINE_ITEMS_BY_SUPPLIER: Record<string, BillLineItem[]> = {
  sup1: DEFAULT_BILL_LINE_ITEMS['BILL-006'] ?? [],
  sup2: DEFAULT_BILL_LINE_ITEMS['BILL-001'] ?? [],
  sup3: DEFAULT_BILL_LINE_ITEMS['BILL-003'] ?? [],
  sup4: DEFAULT_BILL_LINE_ITEMS['BILL-004'] ?? [],
};

function pickDemoItems(supplierId?: string): BillLineItem[] {
  if (supplierId && DEMO_LINE_ITEMS_BY_SUPPLIER[supplierId]?.length > 0) {
    return DEMO_LINE_ITEMS_BY_SUPPLIER[supplierId];
  }
  return DEFAULT_BILL_LINE_ITEMS['BILL-001'] ?? [];
}

// v1.20 — resolution ladder: strong code matches auto-fill internal code;
// fuzzy/suggested matches leave internal empty (partial) for user confirm.
function matchCodes(
  lines: BillLineItem[],
  products: Product[],
  supplierName: string,
  mapLookup?: (supplierCode: string) => string | undefined,
  priceBookLookup?: (supplierCode: string) => import('../data/types').SupplierPriceBookEntry | undefined,
): BillLineItem[] {
  const STRONG = new Set(['remembered', 'pricebook', 'exact']);
  return lines.map(line => {
    const sc = (line.supplierCode || '').trim();
    const match = autoMatchInternalCode({
      supplierCode: sc,
      description: line.description,
      supplierName,
      products,
      priceBookEntry: sc ? priceBookLookup?.(sc) : undefined,
      remembered: mapLookup?.(sc),
    });
    if (match && STRONG.has(match.method)) {
      return { ...line, internalCode: match.internalCode, codeMatch: 'exact' as const, lineType: 'product' as const };
    }
    if (match) {
      return { ...line, internalCode: '', codeMatch: 'partial' as const, lineType: 'product' as const };
    }
    return { ...line, internalCode: '', codeMatch: 'none' as const, lineType: guessLineType(line) };
  });
}


/**
 * v1.49 — the reconcile view can now be EMBEDDED inside the PO page
 * (POPrintPage → Bills section → inline Reconciliation workspace).
 * When embedded:
 *   • `billId` overrides the :id route param
 *   • the page-level chrome (Back · title · linked-PO block) is hidden —
 *     the PO page already provides that context
 *   • switching between sibling bills / closing stays INSIDE the PO page
 *     via onSwitchBill / onClose instead of router navigation
 * The reconciliation table + visual matching rules are untouched — same
 * component, same behaviour, just re-homed.
 */
export interface BillReconcileViewProps {
  /** Overrides the :id route param — used when embedded in the PO page. */
  billId?: string;
  /** Render without page chrome for inline use inside the PO page. */
  embedded?: boolean;
  /** Embedded: switch the workspace to a sibling bill instead of navigating. */
  onSwitchBill?: (billId: string) => void;
  /** Embedded: close the inline reconciliation workspace. */
  onClose?: () => void;
}

export const BillReconcileView: React.FC<BillReconcileViewProps> = ({
  billId, embedded = false, onSwitchBill, onClose,
}) => {
  const { id: routeId } = useParams<{ id: string }>();
  const id = billId ?? routeId;
  const navigate = useNavigate();

  // Embedded-aware navigation: stay inside the PO page when possible.
  const goToBill = useCallback((targetId: string) => {
    if (embedded && onSwitchBill) onSwitchBill(targetId);
    else navigate(`/bills/${targetId}?mode=reconcile`);
  }, [embedded, onSwitchBill, navigate]);

  // Reconcile view — entered via BillPage when ?mode=reconcile.
  // PO page navigates here (no embedded reconcile — PDF preview stays on this page only).

  const {
    bills, billLineItems, suppliers, purchaseOrders, poLineDetails, products, projects,
    createBill,
    updateBill, setBillLineItems, deleteBillImport, updatePOLineDetails, updatePurchaseOrder,
    addProduct, addSupplierCodeMap, lookupSupplierCode, lookupSupplierPriceBookEntry,
    addSupplierPriceBookEntries, trackSupplierCodes, internalCodePrefix, supplierPriceBook,
    autoLinkBillReference, setAutoLinkBillReference,
    accountingProduct,
  } = useData();

  const bill = bills.find(b => b.id === id);
  const closeView = useCallback(() => {
    if (embedded && onClose) onClose();
    else {
      const poId = bill?.linkedPoId;
      navigate(poId ? `/po-print/${poId}` : '/bills');
    }
  }, [embedded, onClose, navigate, bill?.linkedPoId]);
  const supplier = bill ? suppliers.find(s => s.id === bill.supplierId) : null;
  const linkedPo = bill?.linkedPoId ? purchaseOrders.find(po => po.id === bill.linkedPoId) : null;
  const bookForSupplier = useMemo(
    () => (bill ? supplierPriceBook.filter(e => e.supplierId === bill.supplierId) : []),
    [supplierPriceBook, bill],
  );

  const importPreviewDeps = useMemo((): ImportPreviewDeps | null => {
    if (!bill || !supplier) return null;
    return {
      supplierId: bill.supplierId,
      supplierName: supplier.name,
      products,
      bookForSupplier,
      lookupSupplierCode: (sc: string) => lookupSupplierCode(bill.supplierId, sc),
      internalCodePrefix,
      addProduct,
    };
  }, [bill, supplier, products, bookForSupplier, lookupSupplierCode, addProduct, internalCodePrefix]);

  // Multi-bill: all bills linked to the same PO
  const siblingBills = linkedPo
    ? bills.filter(b => b.linkedPoId === linkedPo.id).sort((a, b) => a.date.localeCompare(b.date))
    : [];
  const billPosition = siblingBills.findIndex(b => b.id === id) + 1;

  // "Find existing bill" dialog (shown in pre-import state)
  const [findBillOpen, setFindBillOpen] = useState(false);
  const [findBillSearch, setFindBillSearch] = useState('');
  const candidateBills = bills.filter(b => {
    if (b.id === id) return false;
    const sup = suppliers.find(s => s.id === b.supplierId);
    const q = findBillSearch.toLowerCase();
    if (!q) return true;
    return (
      b.billNo.toLowerCase().includes(q) ||
      (b.supplierInvoiceNo || '').toLowerCase().includes(q) ||
      (sup?.name || '').toLowerCase().includes(q) ||
      (b.linkedPoId || '').toLowerCase().includes(q) ||
      (b.jobRef || '').toLowerCase().includes(q)
    );
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processStep, setProcessStep] = useState(0);
  const [processed, setProcessed] = useState(bill?.processed ?? false);
  const [parseError, setParseError] = useState<string | null>(null);
  // Structured AI-failure context — distinguishes "credits ran out" / "rate-limited" /
  // "overloaded" / generic-error so we can show a friendlier banner than the raw error text.
  const [aiFailure, setAiFailure] = useState<null | {
    code: 'INSUFFICIENT_CREDITS' | 'RATE_LIMITED' | 'OVERLOADED' | 'BAD_API_KEY' | 'UNKNOWN';
    message: string;
    docsUrl?: string;
  }>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [parsedMeta, setParsedMeta] = useState<{ supplierInvoiceNo?: string; invoiceDate?: string; customerPoRef?: string } | null>(null);

  const [globalGst, setGlobalGst] = useState<'ex' | 'inc'>('ex');
  const [lineGstOverrides, setLineGstOverrides] = useState<Record<number, 'ex' | 'inc'>>({});
  const toggleLineGst = (lineId: number, current: 'ex' | 'inc') => {
    const next = current === 'ex' ? 'inc' : 'ex';
    if (next === globalGst) setLineGstOverrides(prev => { const n = { ...prev }; delete n[lineId]; return n; });
    else setLineGstOverrides(prev => ({ ...prev, [lineId]: next }));
  };
  const applyGlobalToAll = () => setLineGstOverrides({});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [leftTab, setLeftTab] = useState<'po' | 'bill'>('bill');
  const [leftWidth, setLeftWidth] = useState(480);

  /*
   * FI-023 FR-PDF-2 — PDF preview tri-state toggle.
   *
   * Three states the user can rotate through:
   *   'expanded'  → full preview panel (~480px) — default after a parse
   *   'collapsed' → 48px vertical rail with file icon + expand chevron
   *   'hidden'    → preview removed entirely; a 'Show PDF preview'
   *                 button appears at the top of the bill header
   *
   * Persists to localStorage under 'bill-detail-pdf-state'. Migrates
   * the older boolean key 'bill-detail-left-collapsed' transparently.
   */
  type PdfState = 'expanded' | 'collapsed' | 'hidden';
  // v1.10.2 — bumped key to v2 so every user gets the new "expanded by default"
  // behaviour once. Previous saved 'collapsed'/'hidden' preferences are
  // discarded on first load; thereafter the user's choice is respected again.
  // v1.36 — bumped key to v3 so every user gets the new "collapsed by default"
  // behaviour once (side-by-side preview is small/collapsed by default across
  // all four document pages; expand to a large reading pane on demand).
  const [pdfState, setPdfStateRaw] = useState<PdfState>(() => {
    try {
      const v = localStorage.getItem('bill-detail-pdf-state-v3');
      if (v === 'expanded' || v === 'collapsed' || v === 'hidden') return v;
    } catch {}
    return 'collapsed';
  });
  const setPdfState = useCallback((next: PdfState) => {
    setPdfStateRaw(next);
    try { localStorage.setItem('bill-detail-pdf-state-v3', next); } catch {}
  }, []);
  const leftCollapsed = pdfState === 'collapsed';
  const pdfHidden     = pdfState === 'hidden';
  const toggleLeftCollapsed = useCallback(() => {
    // Click the collapse chevron from expanded → collapsed; or from
    // collapsed → expanded. (The 'Hide' state is reached via the
    // separate × button on the expanded panel.)
    setPdfState(pdfState === 'collapsed' ? 'expanded' : 'collapsed');
  }, [pdfState, setPdfState]);
  const [showSalesPrice, setShowSalesPrice] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const paneIsDragging = useRef(false);
  const paneDragStartX = useRef(0);
  const paneDragStartWidth = useRef(0);
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    paneIsDragging.current = true;
    paneDragStartX.current = e.clientX;
    paneDragStartWidth.current = leftWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!paneIsDragging.current) return;
      const delta = ev.clientX - paneDragStartX.current;
      const containerW = containerRef.current?.offsetWidth ?? 1000;
      setLeftWidth(Math.max(300, Math.min(Math.floor(containerW * 0.72), paneDragStartWidth.current + delta)));
    };
    const onUp = () => {
      paneIsDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [leftWidth]);
  const [billSubTab, setBillSubTab] = useState<'pdf' | 'summary'>('pdf');
  const [detailsExpanded, setDetailsExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('bill-reconcile-details-expanded-v1') === '1'; } catch { return false; }
  });
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const toggleDetailsExpanded = () => {
    const next = !detailsExpanded;
    setDetailsExpandedRaw(next);
    try { localStorage.setItem('bill-reconcile-details-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const disclosureKeyDown = (toggle: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  };
  const [pdfViewHeight, setPdfViewHeight] = useState(630);
  const pdfHeightDragging = useRef(false);
  const pdfHeightDragStartY = useRef(0);
  const pdfHeightDragStartH = useRef(0);
  const handlePdfHeightDragStart = useCallback((e: React.MouseEvent) => {
    pdfHeightDragging.current = true;
    pdfHeightDragStartY.current = e.clientY;
    pdfHeightDragStartH.current = pdfViewHeight;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!pdfHeightDragging.current) return;
      setPdfViewHeight(Math.max(200, Math.min(1400, pdfHeightDragStartH.current + (ev.clientY - pdfHeightDragStartY.current))));
    };
    const onUp = () => {
      pdfHeightDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pdfViewHeight]);
  const [billMarkup, setBillMarkup] = useState<string>(String(supplier?.defaultMarkup ?? 65));
  // v1.45 — initialise from the bill's persisted sync flag so receipts already
  // pushed onto the PO aren't offered (or applied) twice. Component remounts per
  // bill (key on id in BillPage), so this init runs fresh for each bill.
  const [backfillApplied, setBackfillApplied] = useState(bill?.reconciledReceiptsSynced ?? false);
  /** Bill lines the user has accepted to add to the PO (preview until Sync). */
  const [acceptedExtras, setAcceptedExtras] = useState<Set<number>>(new Set());
  const [editRef, setEditRef] = useState(bill?.reference ?? bill?.linkedPoId ?? '');
  const [syncedPoLines, setSyncedPoLines] = useState<null | POLineDetail[]>(null);

  // v1.46 — reference-driven linking. After a parse we try to match the bill's
  // reference to a PO / job / project and either auto-link (setting on) or offer it.
  const [linkSuggestion, setLinkSuggestion] = useState<null | { po?: PurchaseOrder; jobRef?: string; project?: Project }>(null);
  const [linkConfirmation, setLinkConfirmation] = useState<string | null>(null);

  const matchReference = useCallback((ref: string, supplierId: string): { po?: PurchaseOrder; jobRef?: string; project?: Project } => {
    const r = (ref || '').trim().toLowerCase();
    if (!r) return {};
    const norm = (v?: string) => (v || '').trim().toLowerCase();
    const poMatches = purchaseOrders.filter(po => {
      const id = norm(po.id);
      return [po.id, po.reference, po.title].some(v => norm(v) && norm(v) === r)
        || (id && (r.includes(id) || id.includes(r)));
    });
    const po = poMatches.find(p => p.supplierId === supplierId) ?? poMatches[0];
    const jobRef = po?.jobRef
      || purchaseOrders.find(p => norm(p.jobRef) && (norm(p.jobRef) === r || r.includes(norm(p.jobRef))))?.jobRef
      || undefined;
    const project = projects.find(pr =>
      (norm(pr.number) && (norm(pr.number) === r || r.includes(norm(pr.number))))
      || (norm(pr.name) && norm(pr.name) === r)
      || (po?.projectId ? pr.id === po.projectId : false),
    ) || undefined;
    return { po, jobRef, project };
  }, [purchaseOrders, projects]);

  // Sync mismatch dialog state
  interface SyncMatchedItem {
    poLine: POLineDetail;
    billLine: BillLineItem;
    qtyMatch: boolean;
    priceVariance: number;
    priceVariancePct: number;
    priceFlag: 'ok' | 'minor' | 'moderate' | 'significant';
  }
  interface PriceMatchItem {
    poLine: POLineDetail;
    billLine: BillLineItem;
  }
  interface SyncAnalysis {
    matched: SyncMatchedItem[];
    billOnly: BillLineItem[];
    poOnly: POLineDetail[];
    priceMatches: PriceMatchItem[];
  }
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncAnalysis, setSyncAnalysis] = useState<SyncAnalysis | null>(null);
  // P0.1: false = update received qty only; true = update both ordered + received
  const [syncUpdateOrdered, setSyncUpdateOrdered] = useState(false);
  // Receipt All items: master toggle, defaulted ON — selects every receivable line
  const [syncReceiptAll, setSyncReceiptAll] = useState(true);
  // P1.1: per-line checkbox selection keyed by 'matched-{code}' or 'bill-{id}'
  const [syncChecked, setSyncChecked] = useState<Set<string>>(new Set());

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const toggleSelect = (lid: number) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(lid)) n.delete(lid); else n.add(lid); return n; });
  const toggleSelectAll = () => { if (selectedIds.size === lineItems.length) setSelectedIds(new Set()); else setSelectedIds(new Set(lineItems.map(l => l.id))); };
  const deleteSelected = () => { setLineItems(prev => prev.filter(l => !selectedIds.has(l.id))); setSelectedIds(new Set()); };
  const deleteLine = (lid: number) => { setLineItems(prev => prev.filter(l => l.id !== lid)); setSelectedIds(prev => { const n = new Set(prev); n.delete(lid); return n; }); };

  // ── Inline edits: PO qty / received / bill qty — save immediately ──────────
  const updateBillLineQty = useCallback((lineId: number, nextQty: number) => {
    if (!bill) return;
    setLineItems(prev => {
      const updated = prev.map(l => {
        if (l.id !== lineId) return l;
        const newLineTotal = +(nextQty * l.unitPrice).toFixed(2);
        const ratio = l.lineTotal > 0 ? l.gstAmount / l.lineTotal : 0.1;
        const newGstAmount = +(newLineTotal * ratio).toFixed(2);
        return { ...l, qty: nextQty, lineTotal: newLineTotal, gstAmount: newGstAmount };
      });
      setBillLineItems(bill.id, updated);
      const subT = updated.reduce((s, l) => s + l.lineTotal, 0);
      const gstT = updated.reduce((s, l) => s + l.gstAmount, 0);
      updateBill(bill.id, { subtotalExGst: subT, totalGst: gstT, totalIncGst: subT + gstT });
      return updated;
    });
  }, [bill, setBillLineItems, updateBill]);

  const updatePoLineField = useCallback((
    poLineInventoryCode: string,
    field: 'orderedQty' | 'actualQty' | 'receivedToDate' | 'unitPrice',
    nextValue: number
  ) => {
    if (!linkedPo) return;
    const key = (poLineInventoryCode || '').trim().toUpperCase();
    const matches = (l: POLineDetail) =>
      (l.inventoryCode || '').trim().toUpperCase() === key ||
      (l.supplierCode || '').trim().toUpperCase() === key;
    const existing = syncedPoLines ?? poLineDetails.filter(l => l.poId === linkedPo.id);
    const updated = existing.map(l => {
      if (!matches(l)) return l;
      // When ordered qty changes and actual was tracking it, keep them in step
      if (field === 'orderedQty' && (l.actualQty ?? l.orderedQty) === l.orderedQty) {
        return { ...l, orderedQty: nextValue, actualQty: nextValue };
      }
      return { ...l, [field]: nextValue };
    });
    updatePOLineDetails(linkedPo.id, updated);
    setSyncedPoLines(updated);
  }, [linkedPo, poLineDetails, syncedPoLines, updatePOLineDetails]);
  const deleteAll = () => {
    setLineItems([]); setSelectedIds(new Set()); setProcessed(false);
    setUploadedFile(null); setPdfUrl(null); setParsedMeta(null);
    setSyncedPoLines(null); setBackfillApplied(false);
    // Persist deletion to context/localStorage
    if (bill) deleteBillImport(bill.id);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lineItems, setLineItems] = useState<BillLineItem[]>(
    (bill && billLineItems[bill.id]) ? billLineItems[bill.id] : []
  );

  // Unallocated-code resolution (v1.17) — which bill line (by id) is open.
  const [resolveBillLineId, setResolveBillLineId] = useState<number | null>(null);

  // ── Unallocated-code resolution on the Bill side (v1.17) ───────────────────
  // Resolve a bill line's supplier code to an internal code (match existing or
  // create new), keyed by the stable bill-line id. Optionally remember the
  // mapping per-supplier for future auto-matching.
  const upsertBillPriceBook = useCallback((items: BillLineItem[]) => {
    if (!bill) return;
    addSupplierPriceBookEntries(
      items.filter(l => (l.supplierCode || '').trim()).map(l => ({
        supplierId: bill.supplierId,
        supplierCode: l.supplierCode,
        description: l.description,
        unitPrice: l.unitPrice,
        internalCode: l.internalCode || undefined,
        stocked: l.lineType !== 'service',
        source: 'bill' as const,
      }))
    );
  }, [bill, addSupplierPriceBookEntries]);

  const handleResolveBillLine = useCallback((lineId: number, internalCode: string, remember: boolean) => {
    const target = lineItems.find(l => l.id === lineId);
    setLineItems(prev => {
      const next = prev.map(l => l.id === lineId ? { ...l, internalCode, codeMatch: 'exact' as const, lineType: 'product' as const } : l);
      if (bill) {
        setBillLineItems(bill.id, next);
        upsertBillPriceBook(next);
      }
      return next;
    });
    if (remember && target?.supplierCode && bill) {
      addSupplierCodeMap({ supplierId: bill.supplierId, supplierCode: target.supplierCode, internalCode });
    }
    // v1.18 — link the supplier price-book entry to the internal code (upsert).
    if (target?.supplierCode && bill) {
      addSupplierPriceBookEntries([{
        supplierId: bill.supplierId, supplierCode: target.supplierCode,
        description: target.description, unitPrice: target.unitPrice,
        internalCode, stocked: false, source: 'bill',
      }]);
    }
    setResolveBillLineId(null);
  }, [lineItems, bill, setBillLineItems, addSupplierCodeMap, addSupplierPriceBookEntries, upsertBillPriceBook]);

  const handleMarkBillLineService = useCallback((lineId: number) => {
    setLineItems(prev => {
      const next = prev.map(l => l.id === lineId ? { ...l, lineType: 'service' as const, internalCode: '', codeMatch: 'none' as const } : l);
      if (bill) {
        setBillLineItems(bill.id, next);
        upsertBillPriceBook(next);
      }
      return next;
    });
    setResolveBillLineId(null);
  }, [bill, setBillLineItems, upsertBillPriceBook]);

  const handleAutoCreateBillLine = useCallback((lineId: number) => {
    if (!bill || !supplier) return;
    const line = lineItems.find(l => l.id === lineId);
    if (!line) return;
    const sc = (line.supplierCode || '').trim();
    const entry = sc ? lookupSupplierPriceBookEntry(bill.supplierId, sc) : undefined;
    const result = autoMatchOrCreate({
      supplierCode: sc,
      description: line.description,
      supplierName: supplier.name,
      products,
      priceBookEntry: entry,
      remembered: sc ? lookupSupplierCode(bill.supplierId, sc) : undefined,
      internalCodePrefix,
    });
    if (result.method === 'created') {
      addProduct({
        code: result.internalCode,
        description: line.description,
        category: 'Uncategorised',
        primarySupplier: bill.supplierId,
        purchasePrice: line.unitPrice,
        markup: 45,
        stock: 0,
        minStock: 0,
      });
    }
    handleResolveBillLine(lineId, result.internalCode, true);
  }, [bill, supplier, lineItems, lookupSupplierPriceBookEntry, products, lookupSupplierCode, addProduct, handleResolveBillLine, internalCodePrefix]);

  const handleCreateProductFromBill = useCallback((draft: { code: string; description: string; category: string; purchasePrice: number; markup: number; minStock: number }) => addProduct({
    code: draft.code,
    description: draft.description,
    category: draft.category,
    primarySupplier: bill?.supplierId ?? '',
    purchasePrice: draft.purchasePrice,
    markup: draft.markup,
    stock: 0,
    minStock: draft.minStock,
  }), [addProduct, bill]);

  /*
   * FI-022 FR-3 — post-parse merge/replace/attach modal.
   *
   * When the Bill already has line items and the user re-parses
   * (or drops a second invoice), we never silently overwrite. The
   * parsed-but-not-yet-committed items live in `pendingParse` while
   * a modal asks the user what to do.
   *
   * pendingParse === null         → no decision pending
   * pendingParse !== null         → modal is open, user must choose
   */
  const [pendingParse, setPendingParse] = useState<{
    items: BillLineItem[];
    sourceFileName?: string;
    supplierInvoiceNo?: string;
    invoiceDate?: string;
    customerPoRef?: string;
    documentGst?: 'ex' | 'inc';
  } | null>(null);
  const itemsAtParseStart = useRef<number>(0);

  // v1.33 — import preview (PO-parity code resolution before committing bill lines)
  const [pendingBillImport, setPendingBillImport] = useState<{
    items: BillLineItem[];
    sourceFileName?: string;
    supplierInvoiceNo?: string;
    invoiceDate?: string;
    customerPoRef?: string;
    documentGst?: 'ex' | 'inc';
    demo?: boolean;
    demoMessage?: string;
  } | null>(null);
  const [billPreviewResolutions, setBillPreviewResolutions] = useState<Record<number, ImportLineState>>({});
  const [billImportPreviewCollapsed, setBillImportPreviewCollapsed] = useState(true);

  /*
   * FI-022 FR-3 — three resolutions for the post-parse modal.
   * Declared AFTER pendingParse + lineItems state so closures see
   * them at the right declaration order.
   */
  const dedupeBy = (a: BillLineItem, b: BillLineItem) =>
    a.supplierCode === b.supplierCode &&
    a.description.trim().toLowerCase() === b.description.trim().toLowerCase() &&
    a.qty === b.qty;

  const persistBillTotals = useCallback((items: BillLineItem[]) => {
    if (!bill) return;
    setBillLineItems(bill.id, items);
    upsertBillPriceBook(items);
    const subT = items.reduce((s, l) => s + l.lineTotal, 0);
    const gstT = items.reduce((s, l) => s + l.gstAmount, 0);
    updateBill(bill.id, { processed: true, pdfUploaded: true, subtotalExGst: subT, totalGst: gstT, totalIncGst: subT + gstT });
    // v1.14 — PO state is set by the user's explicit "Goods received?" answer
    // on the reconcile panel, not automatically when totals are persisted.
  }, [bill, setBillLineItems, updateBill, upsertBillPriceBook]);

  const handleMergeParse = useCallback(() => {
    if (!pendingParse) return;
    const merged: BillLineItem[] = [...lineItems];
    pendingParse.items.forEach((p) => {
      if (!merged.some((m) => dedupeBy(m, p))) merged.push(p);
    });
    setLineItems(merged);
    setProcessed(true);
    persistBillTotals(merged);
    setPendingParse(null);
    setBackfillApplied(false); // new lines arrived — allow re-syncing to the PO
    if (bill) updateBill(bill.id, { reconciledReceiptsSynced: false });
    setAcceptedExtras(new Set());
  }, [pendingParse, lineItems, persistBillTotals]);

  const handleReplaceParse = useCallback(() => {
    if (!pendingParse) return;
    setLineItems(pendingParse.items);
    setProcessed(true);
    persistBillTotals(pendingParse.items);
    setPendingParse(null);
    setBackfillApplied(false); // new lines arrived — allow re-syncing to the PO
    if (bill) updateBill(bill.id, { reconciledReceiptsSynced: false });
    setAcceptedExtras(new Set());
  }, [pendingParse, persistBillTotals]);

  const handleJustAttachParse = useCallback(() => {
    setPendingParse(null);
    setProcessed(lineItems.length > 0);
  }, [lineItems.length]);

  // v1.45 — multiple bills → one PO. Instead of merging/replacing the current
  // bill, keep the freshly-parsed document as its OWN bill linked to the same PO.
  const handleNewBillParse = useCallback(() => {
    if (!pendingParse || !bill || !linkedPo) return;
    const items = pendingParse.items;
    const newId = createBill({
      supplierId: bill.supplierId,
      linkedPoId: linkedPo.id,
      jobRef: bill.jobRef,
    });
    setBillLineItems(newId, items);
    const subT = items.reduce((s, l) => s + l.lineTotal, 0);
    const gstT = items.reduce((s, l) => s + l.gstAmount, 0);
    updateBill(newId, {
      processed: true,
      pdfUploaded: true,
      subtotalExGst: subT,
      totalGst: gstT,
      totalIncGst: subT + gstT,
      reconciledReceiptsSynced: false,
      ...(pendingParse.supplierInvoiceNo ? { supplierInvoiceNo: pendingParse.supplierInvoiceNo } : {}),
      ...(pendingParse.invoiceDate ? { date: pendingParse.invoiceDate } : {}),
    });
    setPendingParse(null);
    goToBill(newId);
  }, [pendingParse, bill, linkedPo, createBill, setBillLineItems, updateBill, goToBill]);

  // v1.46 — accept the offered reference match (optionally remember the choice).
  const applyLinkSuggestion = useCallback((opts?: { always?: boolean }) => {
    if (!linkSuggestion || !bill) return;
    if (opts?.always) setAutoLinkBillReference(true);
    updateBill(bill.id, {
      ...(linkSuggestion.po ? { linkedPoId: linkSuggestion.po.id } : {}),
      ...(linkSuggestion.jobRef ? { jobRef: linkSuggestion.jobRef } : {}),
      ...(linkSuggestion.project ? { projectId: linkSuggestion.project.id } : {}),
    });
    setLinkConfirmation(
      linkSuggestion.po
        ? `Matched to ${linkSuggestion.po.id}${linkSuggestion.jobRef ? ` · job ${linkSuggestion.jobRef}` : ''}.`
        : linkSuggestion.jobRef
          ? `Matched to job ${linkSuggestion.jobRef}.`
          : `Associated with project ${linkSuggestion.project!.number}.`,
    );
    setLinkSuggestion(null);
  }, [linkSuggestion, bill, updateBill, setAutoLinkBillReference]);

  // Start an empty additional bill against the same PO (user then drops the doc).
  const handleAddAnotherBill = useCallback(() => {
    if (!bill || !linkedPo) return;
    const newId = createBill({
      supplierId: bill.supplierId,
      linkedPoId: linkedPo.id,
      jobRef: bill.jobRef,
    });
    goToBill(newId);
  }, [bill, linkedPo, createBill, goToBill]);

  const applyParsedBill = useCallback((
    resolutionsOverride?: Record<number, ImportLineState>,
    importOverride?: typeof pendingBillImport,
    codeMode: 'useSupplierCodes' | 'autoCreate' = 'autoCreate',
  ) => {
    const pending = importOverride ?? pendingBillImport;
    if (!pending || !bill || !supplier || !importPreviewDeps) return;
    const resolutions = resolutionsOverride ?? billPreviewResolutions;
    const mintedInBatch: string[] = [];
    const createdSupplierAsInternal = new Set<string>();
    const items: BillLineItem[] = pending.items
      .filter(l => (l.supplierCode || '').trim() || (l.description || '').trim())
      .map((l, i) => {
        const sc = (l.supplierCode || '').trim();
        const res0 = resolutions[i] ?? buildImportLineResolution(l, importPreviewDeps);
        const isService = res0.status === 'service' || res0.userAction === 'service'
          || guessLineType(l) === 'service';

        if (codeMode === 'useSupplierCodes') {
          const lineType: 'product' | 'service' = isService ? 'service' : 'product';
          const internalCode = lineType === 'service' ? '' : sc;
          if (internalCode && !products.some(p => p.code === internalCode) && !createdSupplierAsInternal.has(internalCode)) {
            createdSupplierAsInternal.add(internalCode);
            addProduct({
              code: internalCode,
              description: l.description,
              category: 'Uncategorised',
              primarySupplier: bill.supplierId,
              purchasePrice: l.unitPrice ?? 0,
              markup: 45,
              stock: 0,
              minStock: 0,
            });
          }
          return {
            ...l,
            supplierCode: trackSupplierCodes ? sc : '',
            internalCode,
            lineType,
            codeMatch: (lineType !== 'service' && sc ? 'exact' : 'none') as BillLineItem['codeMatch'],
          };
        }

        let res = res0;
        if (res.status === 'new' && res.userAction === 'pending') {
          res = { ...res, userAction: 'autoCreate' };
        }
        const { internalCode, lineType } = resolveImportPreviewInternal(l, res, importPreviewDeps, mintedInBatch);
        const codeMatch: BillLineItem['codeMatch'] =
          lineType === 'service' ? 'none'
            : internalCode && (res.status === 'matched' || res.userAction === 'confirmSuggested' || res.userAction === 'autoCreate')
              ? 'exact'
              : internalCode ? 'partial' : 'none';
        return {
          ...l,
          supplierCode: trackSupplierCodes ? sc : '',
          internalCode: lineType === 'service' ? '' : internalCode,
          lineType,
          codeMatch,
        };
      });

    if (trackSupplierCodes) {
      addSupplierPriceBookEntries(
        items.filter(l => l.supplierCode).map(l => ({
          supplierId: bill.supplierId,
          supplierCode: l.supplierCode,
          description: l.description,
          unitPrice: l.unitPrice,
          internalCode: l.internalCode || undefined,
          stocked: l.lineType !== 'service',
          source: 'bill' as const,
        })),
      );
      items.forEach(l => {
        if (l.supplierCode && l.internalCode) {
          addSupplierCodeMap({ supplierId: bill.supplierId, supplierCode: l.supplierCode, internalCode: l.internalCode });
        }
      });
    }

    const saved = { ...pending };
    setPendingBillImport(null);
    setBillPreviewResolutions({});

    if (itemsAtParseStart.current > 0) {
      setPendingParse({
        items,
        sourceFileName: saved.sourceFileName,
        supplierInvoiceNo: saved.supplierInvoiceNo,
        invoiceDate: saved.invoiceDate,
        customerPoRef: saved.customerPoRef,
        documentGst: saved.documentGst,
      });
      return;
    }

    setLineItems(items);
    setProcessed(true);
    persistBillTotals(items);
    setBackfillApplied(false);
    if (bill) updateBill(bill.id, { reconciledReceiptsSynced: false });
    setAcceptedExtras(new Set());
  }, [
    pendingBillImport, bill, supplier, importPreviewDeps, billPreviewResolutions,
    trackSupplierCodes, products, addProduct,
    addSupplierPriceBookEntries, addSupplierCodeMap, persistBillTotals, updateBill,
  ]);

  // Always open pre-parse so the user picks: use supplier codes OR auto-create
  // unique internal codes (PO-import parity).
  const showBillImportPreview = useCallback((
    items: BillLineItem[],
    file: File | null,
    meta: {
      supplierInvoiceNo?: string;
      invoiceDate?: string;
      customerPoRef?: string;
      documentGst?: 'ex' | 'inc';
    },
    demo: boolean,
    demoMessage?: string,
  ) => {
    if (!importPreviewDeps) return;
    setPendingBillImport({ items, sourceFileName: file?.name, demo, demoMessage, ...meta });
    setBillPreviewResolutions(buildInitialImportResolutions(items, importPreviewDeps));
  }, [importPreviewDeps]);

  const discardBillImportPreview = useCallback(() => {
    setPendingBillImport(null);
    setBillPreviewResolutions({});
  }, []);

  const setBillPreviewAction = useCallback((idx: number, action: ImportUserAction) => {
    if (!pendingBillImport || !importPreviewDeps) return;
    setBillPreviewResolutions(prev =>
      applyImportPreviewAction(pendingBillImport.items, prev, idx, action, importPreviewDeps));
  }, [pendingBillImport, importPreviewDeps]);

  const batchBillPreviewAction = useCallback((action: 'autoCreate' | 'service') => {
    if (!pendingBillImport || !importPreviewDeps) return;
    setBillPreviewResolutions(prev =>
      batchImportPreviewAction(pendingBillImport.items, prev, action, importPreviewDeps));
  }, [pendingBillImport, importPreviewDeps]);

  const useSupplierCodesAndApplyBill = useCallback(() => {
    applyParsedBill(undefined, undefined, 'useSupplierCodes');
  }, [applyParsedBill]);

  const autoCreateInternalCodesAndApplyBill = useCallback(() => {
    applyParsedBill(undefined, undefined, 'autoCreate');
  }, [applyParsedBill]);

  const billPreviewNewCount = pendingBillImport && importPreviewDeps
    ? countPreviewNewLines(pendingBillImport.items, billPreviewResolutions, importPreviewDeps)
    : 0;

  const processFile = useCallback((file: File) => {
    setUploadedFile(file);
    if (file.type === 'application/pdf') setPdfUrl(URL.createObjectURL(file));
    else if (file.type.startsWith('image/')) setPdfUrl(URL.createObjectURL(file));
    else setPdfUrl(null);
    setLeftTab('bill');

    // Capture the count of existing items BEFORE the parse runs.
    // After parse completes, if this is > 0 we don't auto-commit —
    // instead we show the merge/replace/attach modal (FI-022 FR-3).
    itemsAtParseStart.current = lineItems.length;

    setTimeout(() => {
      setProcessing(true); setProcessStep(0); setParseError(null); setAiFailure(null); setDemoMode(false);
      let stepIdx = 0;
      const stepInterval = setInterval(() => { stepIdx = Math.min(stepIdx + 1, PROCESSING_STEPS.length - 2); setProcessStep(stepIdx); }, 900);
      const formData = new FormData(); formData.append('file', file);
      fetch('/api/parse-invoice', { method: 'POST', body: formData })
        .then(async response => {
          if (response.ok) {
            const data = await response.json();
            // v1.15.3 — server now returns 200 with structured error/code in
            // the body when Claude itself fails (we had to commit headers
            // early to bypass Heroku's 30s H12 router timeout). Honour those
            // structured failures here so the right banner / friendly
            // message is shown instead of a generic "parse failed".
            if (data.code) {
              setAiFailure({ code: data.code, message: data.friendly ?? data.error, docsUrl: data.docsUrl });
              setDemoMode(true);
              return {
                rawLines: pickDemoItems(bill?.supplierId),
                parseMeta: {},
                demo: true as const,
                demoMessage: `${data.friendly ?? data.error}`,
              };
            }
            if (data.error) throw new Error(data.error);
            const rawLines: BillLineItem[] = data.lines ?? [];
            const parseMeta = {
              supplierInvoiceNo: data.supplierInvoiceNo as string | undefined,
              invoiceDate: data.invoiceDate as string | undefined,
              customerPoRef: data.customerPoRef as string | undefined,
              documentGst: (data.documentGst === 'inc' ? 'inc' : 'ex') as 'ex' | 'inc',
            };
            setParsedMeta({
              supplierInvoiceNo: parseMeta.supplierInvoiceNo,
              invoiceDate: parseMeta.invoiceDate,
              customerPoRef: parseMeta.customerPoRef,
            });
            if (parseMeta.documentGst === 'inc') setGlobalGst('inc'); else setGlobalGst('ex');
            if (parseMeta.customerPoRef && bill) {
              updateBill(bill.id, { reference: parseMeta.customerPoRef });
              setEditRef(parseMeta.customerPoRef);
              // v1.46 — reference-driven linking. If the bill isn't linked yet,
              // match the reference to a PO / job / project and auto-link (setting
              // on) or offer the match inline.
              if (!bill.linkedPoId) {
                const match = matchReference(parseMeta.customerPoRef, bill.supplierId);
                if (match.po || match.jobRef || match.project) {
                  if (autoLinkBillReference) {
                    updateBill(bill.id, {
                      ...(match.po ? { linkedPoId: match.po.id } : {}),
                      ...(match.jobRef ? { jobRef: match.jobRef } : {}),
                      ...(match.project ? { projectId: match.project.id } : {}),
                    });
                    setLinkConfirmation(
                      match.po
                        ? `Matched to ${match.po.id}${match.jobRef ? ` · job ${match.jobRef}` : ''} — linked automatically from reference "${parseMeta.customerPoRef}".`
                        : match.jobRef
                          ? `Matched to job ${match.jobRef} — linked automatically from reference "${parseMeta.customerPoRef}".`
                          : `Associated with project ${match.project!.number} — linked automatically from reference "${parseMeta.customerPoRef}".`,
                    );
                  } else {
                    setLinkSuggestion(match);
                  }
                }
              }
            }
            return { rawLines, parseMeta, demo: false as const };
          }
          // Structured failure — drop into demo mode but capture the failure type
          // so the banner can show a friendly message (no scary raw vendor error).
          const errBody = await response.json().catch(() => ({ error: 'Unknown server error' }));
          if (errBody.code) {
            setAiFailure({ code: errBody.code, message: errBody.friendly ?? errBody.error, docsUrl: errBody.docsUrl });
            setDemoMode(true);
            return { rawLines: pickDemoItems(bill?.supplierId), parseMeta: {}, demo: true as const,
              demoMessage: `${errBody.friendly ?? errBody.error}` };
          }
          if (response.status === 503) {
            setDemoMode(true);
            return { rawLines: pickDemoItems(bill?.supplierId), parseMeta: {}, demo: true as const };
          }
          throw new Error(errBody.error ?? 'Parse failed');
        })
        .then(result => {
          clearInterval(stepInterval);
          setProcessStep(PROCESSING_STEPS.length - 1);
          setTimeout(() => {
            setProcessing(false);
            if (!result || !result.rawLines?.length) {
              setParseError('No line items found on this document');
              return;
            }
            // v1.33 — PO-parity import preview: resolve supplier codes before commit
            showBillImportPreview(
              result.rawLines,
              file,
              result.parseMeta ?? {},
              result.demo,
              result.demo ? (result.demoMessage || 'AI parsing unavailable — demo lines shown') : undefined,
            );
          }, 400);
        })
        .catch(err => {
          clearInterval(stepInterval);
          const msg = err instanceof Error ? err.message : 'Unknown error';
          setParseError(msg);
          const demoItems = pickDemoItems(bill?.supplierId);
          setDemoMode(true);
          setProcessStep(PROCESSING_STEPS.length - 1);
          setTimeout(() => {
            setProcessing(false);
            showBillImportPreview(demoItems, file, {}, true, msg);
          }, 300);
        });
    }, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill?.supplierId, bill?.linkedPoId, autoLinkBillReference, matchReference, showBillImportPreview]);

  const handleDrop = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); const file = e.dataTransfer.files[0]; if (file) processFile(file); }, [processFile]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) processFile(file); };

  // Set equal pane widths on mount
  React.useEffect(() => {
    if (containerRef.current) {
      setLeftWidth(Math.floor(containerRef.current.offsetWidth / 2));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v1.47 — if the user dropped a supplier invoice on the Bill document view,
  // it was stashed for handoff; consume it once here and run the normal import.
  React.useEffect(() => {
    const handoff = consumePendingBillImportFile();
    if (handoff) processFile(handoff);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v1.52.12 — + in the side-by-side Match column adds the invoice line to the
  // PO immediately (ordered + received = billed qty), not just a preview accept.
  // Must sit above the early return so hooks stay unconditional.
  const addBillLinesToPo = useCallback((billLineIds: number[]) => {
    if (!linkedPo || billLineIds.length === 0) return;
    const idSet = new Set(billLineIds);
    const toAdd = lineItems.filter(bl => idSet.has(bl.id));
    if (toAdd.length === 0) return;

    const existing = syncedPoLines ?? poLineDetails.filter(l => l.poId === linkedPo.id);
    const existingCodes = new Set(
      existing.map(l => (l.inventoryCode || l.supplierCode || '').trim().toUpperCase()).filter(Boolean),
    );

    const newItems: POLineDetail[] = [];
    for (const bl of toAdd) {
      const code = (bl.internalCode || bl.supplierCode || '').trim();
      const key = code.toUpperCase();
      if (key && existingCodes.has(key)) continue; // already on PO
      newItems.push({
        poId: linkedPo.id,
        inventoryCode: bl.lineType === 'service' ? '' : (bl.internalCode || bl.supplierCode || ''),
        lineType: bl.lineType === 'service' ? 'service' as const : 'product' as const,
        supplierCode: bl.supplierCode || undefined,
        description: bl.description,
        orderedQty: bl.qty,
        actualQty: bl.qty,
        receivedToDate: bl.qty,
        unitPrice: bl.unitPrice,
        discount: 0,
      });
      if (key) existingCodes.add(key);
    }
    if (newItems.length === 0) {
      setAcceptedExtras(prev => {
        const next = new Set(prev);
        billLineIds.forEach(id => next.add(id));
        return next;
      });
      return;
    }

    const finalLines = [...existing, ...newItems];
    setSyncedPoLines(finalLines);
    updatePOLineDetails(linkedPo.id, finalLines);
    const exGst = finalLines.reduce((s, l) => s + l.orderedQty * l.unitPrice * (1 - (l.discount || 0) / 100), 0);
    const gst = +(exGst * 0.1).toFixed(2);
    updatePurchaseOrder(linkedPo.id, {
      lines: finalLines.length,
      exGst: +exGst.toFixed(2),
      gst,
      total: +(exGst + gst).toFixed(2),
    });
  }, [linkedPo, lineItems, syncedPoLines, poLineDetails, updatePOLineDetails, updatePurchaseOrder]);

  /** One-click: set Received = bill qty on every matched PO line (no per-row clicks). */
  const handleReceiveAllMatched = useCallback(() => {
    if (!linkedPo) return;
    const poLines = syncedPoLines ?? poLineDetails.filter(l => l.poId === linkedPo.id);
    const { matched } = buildReconcileBands(lineItems, poLines, acceptedExtras);
    const targets = new Map<string, number>();
    for (const m of matched) {
      if (!m.poLine || m.previewAdd) continue;
      const key = (m.poLine.inventoryCode || m.poLine.supplierCode || '').trim().toUpperCase();
      if (!key) continue;
      targets.set(key, Math.max(targets.get(key) ?? 0, m.billLine.qty));
    }
    if (targets.size === 0) return;
    const updated = poLines.map(l => {
      const key = (l.inventoryCode || l.supplierCode || '').trim().toUpperCase();
      const billQty = targets.get(key);
      if (billQty == null) return l;
      return { ...l, receivedToDate: Math.max(l.receivedToDate, billQty) };
    });
    updatePOLineDetails(linkedPo.id, updated);
    setSyncedPoLines(updated);
    const allReceived = updated.every(l => l.receivedToDate >= l.orderedQty);
    const anyReceived = updated.some(l => l.receivedToDate > 0);
    if (allReceived && updated.length > 0) updatePurchaseOrder(linkedPo.id, { status: 'All received' });
    else if (anyReceived) updatePurchaseOrder(linkedPo.id, { status: 'Partial received' });
  }, [
    linkedPo, lineItems, acceptedExtras, syncedPoLines,
    poLineDetails, updatePOLineDetails, updatePurchaseOrder,
  ]);

  if (!bill) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="error">Bill not found.</Alert>
      <Button onClick={closeView} sx={{ mt: 2 }}>{embedded ? 'Close' : 'Back to Bills'}</Button>
    </Box>
  );

  const backToPoPath = linkedPo ? `/po-print/${linkedPo.id}` : null;
  const handleStandaloneBack = () => navigate(backToPoPath ?? '/bills');
  const backLabel = linkedPo ? `Back to ${linkedPo.id}` : 'Back';

  const subTotal = lineItems.reduce((s, l) => s + l.lineTotal, 0);
  const totalGst = lineItems.reduce((s, l) => s + l.gstAmount, 0);
  // Derived from persisted copiedToJob flag on bill line items
  const totalInc = subTotal + totalGst;
  const markupPct = parseFloat(billMarkup) || 0;
  const salesOf = (price: number) => price * (1 + markupPct / 100);

  // Reconciliation: match each bill line to its PO line by internal code
  const poLinesForRecon = linkedPo
    ? (syncedPoLines ?? poLineDetails.filter(l => l.poId === linkedPo.id))
    : [];

  const billImported = processed && lineItems.length > 0;
  const hasPOItems   = !!(linkedPo && poLinesForRecon.length > 0);
  const reconcileStep = resolveReconcileStep({
    processed,
    billImported,
    linkedPoId: bill.linkedPoId,
    backfillApplied,
    status: bill.status,
  });
  const nextHint = reconcileNextAction(reconcileStep, bill.status);
  const showFindExistingBill = !bill.linkedPoId;

  const reconRows: ReconRow[] = linkedPo
    ? billImported
      // Post-import: bill-matched rows + PO-only rows appended at bottom
      ? [
          ...lineItems.map(billLine => ({
            billLine,
            poLine: poLinesForRecon.find(p =>
              billLine.internalCode
                ? p.inventoryCode === billLine.internalCode
                : p.inventoryCode === billLine.supplierCode
            ) ?? null,
          })),
          ...poLinesForRecon
            .filter(p => !lineItems.some(bl =>
              bl.internalCode ? bl.internalCode === p.inventoryCode : bl.supplierCode === p.inventoryCode
            ))
            .map(poLine => ({ billLine: null as BillLineItem | null, poLine })),
        ]
      // Pre-import: show PO lines only, bill side is empty placeholder
      : poLinesForRecon.map(poLine => ({ billLine: null as BillLineItem | null, poLine }))
    : [];

  // Analyse bill vs existing PO before syncing — P0.2: includes price variance
  const analyzeSyncMismatch = (): SyncAnalysis => {
    const existingPo = poLineDetails.filter(l => l.poId === linkedPo?.id);
    // Normalised code comparison. A bill line matches a PO line when EITHER its
    // internal code OR its supplier code equals the PO inventory code. Previously
    // only internalCode was checked, so identical codes carried solely on
    // supplierCode (e.g. 16M1008 = 16M1008) fell through to the "different code"
    // price-match bucket and were mislabelled. Treating them as real matches here
    // fixes that — they now surface as same-code matches, not "different code".
    const norm = (s?: string) => (s || '').trim().toUpperCase();
    const codeMatch = (bl: BillLineItem, p: POLineDetail) => {
      const pc = norm(p.inventoryCode);
      return pc !== '' && (norm(bl.internalCode) === pc || norm(bl.supplierCode) === pc);
    };
    const matched: SyncAnalysis['matched'] = [];
    const billOnly: BillLineItem[] = [];
    for (const bl of lineItems) {
      const pl = existingPo.find(p => codeMatch(bl, p));
      if (pl) {
        const priceVariance = bl.unitPrice - pl.unitPrice;
        const priceVariancePct = pl.unitPrice > 0 ? (priceVariance / pl.unitPrice) * 100 : 0;
        const absPct = Math.abs(priceVariancePct);
        const priceFlag: SyncMatchedItem['priceFlag'] =
          absPct <= 2 ? 'ok' : absPct <= 5 ? 'minor' : absPct <= 10 ? 'moderate' : 'significant';
        matched.push({ poLine: pl, billLine: bl, qtyMatch: pl.orderedQty === bl.qty, priceVariance, priceVariancePct, priceFlag });
      } else {
        billOnly.push(bl);
      }
    }
    const poOnly = existingPo.filter(p => !lineItems.some(b => codeMatch(b, p)));

    // Price-match rule: bill lines with no code match but same price as an unmatched PO line
    const priceMatches: PriceMatchItem[] = [];
    const usedPoLines = new Set<string>();
    const remainingBillOnly: BillLineItem[] = [];
    for (const bl of billOnly) {
      const candidate = poOnly.find(pl =>
        !usedPoLines.has(pl.inventoryCode) &&
        Math.abs(pl.unitPrice - bl.unitPrice) < 0.01
      );
      if (candidate) {
        priceMatches.push({ poLine: candidate, billLine: bl });
        usedPoLines.add(candidate.inventoryCode);
      } else {
        remainingBillOnly.push(bl);
      }
    }
    const remainingPoOnly = poOnly.filter(pl => !usedPoLines.has(pl.inventoryCode));

    return { matched, billOnly: remainingBillOnly, poOnly: remainingPoOnly, priceMatches };
  };

  // Every syncable line as checkbox keys — default selection when dialog opens.
  const collectReceiptKeys = (analysis: SyncAnalysis): Set<string> => {
    const keys = new Set<string>();
    analysis.matched.forEach(m => keys.add(`matched-${m.poLine.inventoryCode}`));
    analysis.priceMatches.forEach(pm => keys.add(`price-${pm.billLine.id}-${pm.poLine.inventoryCode}`));
    analysis.billOnly.forEach(bl => keys.add(`bill-${bl.id}`));
    return keys;
  };

  // P0.1 + P1.1 + P1.4: Apply sync using per-line selections
  // updateOrdered=false → received qty only (original ordered qty untouched)
  // updateOrdered=true  → both ordered + received qty updated to bill qty
  const performSync = (analysis: SyncAnalysis, checked: Set<string>, updateOrdered: boolean) => {
    const existingPo = poLineDetails.filter(l => l.poId === linkedPo?.id);

    // Update matched PO lines where checked
    const updatedLines = existingPo.map(pl => {
      // Code-matched items
      const matchedItem = analysis.matched.find(m => m.poLine.inventoryCode === pl.inventoryCode);
      if (matchedItem && checked.has(`matched-${pl.inventoryCode}`)) {
        if (!syncReceiptAll) {
          return updateOrdered
            ? { ...pl, orderedQty: matchedItem.billLine.qty, unitPrice: matchedItem.billLine.unitPrice }
            : { ...pl, unitPrice: matchedItem.billLine.unitPrice };
        }
        // v1.45 — receipts ADD to what prior bills already received on this PO line
        // so two bills against one PO accumulate (5 + 3 = 8), never clobber.
        const received = pl.receivedToDate + matchedItem.billLine.qty;
        return updateOrdered
          ? { ...pl, orderedQty: matchedItem.billLine.qty, receivedToDate: received, unitPrice: matchedItem.billLine.unitPrice }
          : { ...pl, receivedToDate: received, unitPrice: matchedItem.billLine.unitPrice };
      }
      // Price-match items: mark received only — no code or price changes
      const priceMatch = analysis.priceMatches.find(pm => pm.poLine.inventoryCode === pl.inventoryCode);
      if (priceMatch && checked.has(`price-${priceMatch.billLine.id}-${pl.inventoryCode}`)) {
        if (!syncReceiptAll) return pl;
        return { ...pl, receivedToDate: pl.receivedToDate + priceMatch.billLine.qty };
      }
      return pl;
    });

    // Add checked bill-only items as new PO lines. v1.17 — keep supplierCode
    // DISTINCT: only set inventoryCode when a real internal code exists; never
    // dump the supplier code into the internal-code field. Unresolved lines
    // carry an empty inventoryCode so they surface the red "Enter / Select
    // Code" pill on the PO and can be reconciled there.
    const newItems: POLineDetail[] = analysis.billOnly
      .filter(bl => checked.has(`bill-${bl.id}`))
      .map(bl => ({
        poId: linkedPo!.id,
        inventoryCode: bl.lineType === 'service' ? '' : (bl.internalCode || ''),
        lineType: bl.lineType === 'service' ? 'service' as const : 'product' as const,
        supplierCode: bl.supplierCode || undefined,
        description: bl.description,
        orderedQty: bl.qty,
        actualQty: bl.qty,
        // v1.19.1 — add to PO without receipting when "Receipt all" is off.
        receivedToDate: syncReceiptAll ? bl.qty : 0,
        unitPrice: bl.unitPrice,
        discount: 0,
      }));

    // P1.4: Immediate table update — no reload needed
    const finalLines = [...updatedLines, ...newItems];
    setSyncedPoLines(finalLines);
    // Persist to context/localStorage so data survives page refresh
    if (linkedPo) updatePOLineDetails(linkedPo.id, finalLines);
    if (bill) upsertBillPriceBook(lineItems);
    // v1.45 — remember this bill's receipts are now on the PO so re-opening it
    // won't offer (and additively re-apply) the same sync again.
    if (bill) updateBill(bill.id, { reconciledReceiptsSynced: true });
    setBackfillApplied(true);
    setAcceptedExtras(new Set());
    setLeftTab('po');
    setSyncDialogOpen(false);
  };

  const handleAcceptExtra = (billLineId: number) => {
    addBillLinesToPo([billLineId]);
  };

  const handleAcceptAllExtras = () => {
    const { extras } = buildReconcileBands(lineItems, poLinesForRecon, acceptedExtras);
    addBillLinesToPo(extras.map(bl => bl.id));
  };

  const syncMismatchPreview = analyzeSyncMismatch();
  const unresolvedExtras = syncMismatchPreview.billOnly.filter(bl => !acceptedExtras.has(bl.id));
  const syncExtrasResolved = unresolvedExtras.length === 0;

  const handleSyncToPo = () => {
    const analysis = analyzeSyncMismatch();
    if (analysis.billOnly.some(bl => !acceptedExtras.has(bl.id))) return;
    const checked = collectReceiptKeys(analysis);
    analysis.billOnly.forEach(bl => {
      if (acceptedExtras.has(bl.id)) checked.add(`bill-${bl.id}`);
    });
    const hasMismatch = analysis.billOnly.length > 0 || analysis.poOnly.length > 0 ||
      analysis.matched.some(m => !m.qtyMatch || m.priceFlag !== 'ok');
    if (hasMismatch) {
      setSyncAnalysis(analysis);
      setSyncReceiptAll(true);
      setSyncChecked(checked);
      setSyncUpdateOrdered(false);
      setSyncDialogOpen(true);
    } else {
      performSync(analysis, checked, false);
    }
  };

  // v1.48 — approve the bill and push it to the accounting product for payment.
  const sendToAccounting = (alsoApprove: boolean) => {
    if (!bill) return;
    const record = buildAccountingExport(accountingProduct, bill.billNo);
    updateBill(bill.id, {
      ...(alsoApprove ? { status: 'Approved' as BillStatus } : {}),
      accountingExport: record,
    });
    alert(
      `${alsoApprove ? 'Approved and sent' : 'Sent'} to ${accountingProduct} for payment.\n` +
      `${accountingProduct} reference: ${record.ref}`,
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Page header — reconciliation identity + linked PO ─────────────
          Embedded (inside the PO page): the Back button, page title and
          linked-PO block are hidden — the PO page already IS that context.
          The bill identity row (bill no · status · chips) stays. */}
      <Box className="no-print" sx={{ maxWidth: BILL_RECON_MAX_WIDTH, mx: 'auto', width: '100%', mb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap gap={1.5}>
          <Box sx={{ minWidth: 0 }}>
            {!embedded && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button startIcon={<ArrowBackIcon />} onClick={handleStandaloneBack}
                sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666', mr: 0.25,
                  '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
                {backLabel}
              </Button>
              <InfoableButton info={RECON_TABLE_INFO.reconcilePage}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.12rem', color: '#1a1a2e', letterSpacing: -0.2 }}>
                  Supplier bill reconciliation
                </Typography>
              </InfoableButton>
            </Stack>
            )}
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap
              sx={{ mt: embedded ? 0 : 0.75, ml: { xs: 0, sm: embedded ? 0 : 5.75 } }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: FI_ORANGE }}>
                {bill.billNo}
              </Typography>
              <StatusChip
                status={bill.status}
                onClick={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
                deleteIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                onDelete={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
                sx={{ height: 24, cursor: 'pointer' }}
              />
              {bill.jobRef && (
                <Chip size="small" label={bill.jobRef}
                  sx={{ height: 22, fontSize: '0.65rem', fontWeight: 800,
                    backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }} />
              )}
              {bill.accountingExport && (
                <Chip size="small"
                  icon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                  label={`${bill.accountingExport.product} ${bill.accountingExport.ref}${bill.status === 'Paid' ? ' · paid' : ' · awaiting payment'}`}
                  sx={{ height: 22, fontSize: '0.65rem', fontWeight: 800,
                    backgroundColor: bill.status === 'Paid' ? '#e7f6ef' : '#e8f0fe',
                    color: bill.status === 'Paid' ? '#0a6e45' : '#1a56c4',
                    border: `1px solid ${bill.status === 'Paid' ? '#bfe6d2' : '#bcd4f7'}` }} />
              )}
              {(parsedMeta?.supplierInvoiceNo || bill.supplierInvoiceNo) && (
                <Chip size="small"
                  icon={<Sparkle size={10} variant="single" color={BRAND} />}
                  label={`#${parsedMeta?.supplierInvoiceNo || bill.supplierInvoiceNo}`}
                  sx={{ fontSize: '0.65rem', height: 22, fontWeight: 700, backgroundColor: BRAND_LIGHT, color: BRAND }} />
              )}
              {!embedded && siblingBills.length > 1 && (
                <Stack direction="row" spacing={0.3} alignItems="center"
                  sx={{ backgroundColor: '#f5f0ff', borderRadius: 2, border: `1px solid ${BRAND}33`, px: 0.5, py: 0.2 }}>
                  <Button size="small" disabled={billPosition <= 1}
                    onClick={() => goToBill(siblingBills[billPosition - 2].id)}
                    sx={{ minWidth: 0, p: 0.3, color: BRAND }}>
                    <NavigateBeforeIcon sx={{ fontSize: 16 }} />
                  </Button>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: BRAND, px: 0.5, whiteSpace: 'nowrap' }}>
                    {billPosition} of {siblingBills.length}
                  </Typography>
                  <Button size="small" disabled={billPosition >= siblingBills.length}
                    onClick={() => goToBill(siblingBills[billPosition].id)}
                    sx={{ minWidth: 0, p: 0.3, color: BRAND }}>
                    <NavigateNextIcon sx={{ fontSize: 16 }} />
                  </Button>
                </Stack>
              )}
              {!embedded && linkedPo && (
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={handleAddAnotherBill}
                  sx={{ textTransform: 'none', fontSize: '0.66rem', fontWeight: 700, color: BRAND,
                    border: `1px solid ${BRAND}33`, borderRadius: 2, px: 0.8, py: 0.15,
                    '&:hover': { backgroundColor: BRAND_LIGHT, borderColor: BRAND } }}>
                  Add bill to {linkedPo.id}
                </Button>
              )}
            </Stack>
            <Typography sx={{ fontSize: '0.76rem', color: '#666', fontWeight: 600, mt: 0.45, ml: { xs: 0, sm: embedded ? 0 : 5.75 } }}>
              {supplier?.name ?? '—'}
              {' · '}{fmtDate(bill.date)}
              {embedded ? '' : linkedPo ? ` · reconciling against ${linkedPo.id}` : ' · no PO linked yet'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.8} alignItems="flex-end" flexWrap="wrap" useFlexGap>
            {!embedded && linkedPo && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#999',
                  textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.35 }}>
                  Linked purchase order
                </Typography>
                <Button size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                  onClick={() => navigate(`/po-print/${linkedPo.id}`)}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 800,
                    borderColor: FI_ORANGE, color: FI_ORANGE, py: 0.45, fontFamily: 'monospace' }}>
                  {linkedPo.id}
                </Button>
              </Box>
            )}
            {pdfHidden && (
              <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />}
                onClick={() => setPdfState('expanded')}
                sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: BRAND_BORDER, color: BRAND }}>
                Show PDF
              </Button>
            )}
            <InfoableButton info={RECON_FOOTER_INFO.documentView}>
              <Button size="small" variant="outlined"
                endIcon={embedded ? <OpenInNewIcon sx={{ fontSize: 13 }} /> : undefined}
                onClick={() => navigate(`/bills/${bill.id}`)}
                sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: '#d0c5f0', color: BRAND }}>
                {embedded ? 'Open bill page' : 'Document view'}
              </Button>
            </InfoableButton>
          </Stack>
        </Stack>
      </Box>

      <Menu
        anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Set status
          </Typography>
        </Box>
        {BILL_STATUSES.map(s => {
          const tone = STATUS_TONE[getStatusTone(s)];
          const isCurrent = s === bill.status;
          return (
            <MenuItem key={s} selected={isCurrent}
              onClick={() => { updateBill(bill.id, { status: s }); setStatusAnchor(null); }}
              sx={{ py: 0.75, px: 1.5, ...(isCurrent && { backgroundColor: tone.bg }) }}>
              <StatusChip status={s} dense />
            </MenuItem>
          );
        })}
      </Menu>

      <input ref={fileInputRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={handleFileChange} />

      {/* ── Split Pane Layout ───────────────────────────────────────────── */}
      <Box ref={containerRef} sx={{ display: 'flex', gap: 0, alignItems: 'flex-start', maxWidth: BILL_RECON_MAX_WIDTH, mx: 'auto', width: '100%' }}>

        {/* ────────── LEFT PANEL: Toggle PO / Bill ────────── */}
        {/* Left preview pane — width animates to 0 when collapsed (lean pattern) */}
        {!pdfHidden && (
        <Box sx={{
            width: leftCollapsed ? COLLAPSE_THUMB_WIDTH : leftWidth,
            flexShrink: 0, overflow: 'hidden',
            position: 'sticky', top: 16,
            transition: leftCollapsed ? 'width 0.18s ease' : 'none',
          }} className="no-print">

          {leftCollapsed ? (
            <DocumentPreviewRail
              collapsed
              fileName={uploadedFile?.name ?? (processed ? 'Imported invoice' : 'Invoice')}
              fileUrl={pdfUrl}
              fileType={uploadedFile?.type}
              thumbWidth={COLLAPSE_THUMB_WIDTH}
              expandedWidth={leftWidth}
              minHeight={280}
              onExpand={() => setPdfState('expanded')}
            />
          ) : (
          /* Bill PDF / Import panel */
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <input ref={fileInputRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={handleFileChange} />

            {/* ── Sticky top action bar: tabs + Re-import + Delete Import ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', backgroundColor: '#fafafa', px: 0.5, minHeight: 36 }}>
              {(pdfUrl || processed) ? (
                <Stack direction="row" sx={{ flex: 1 }}>
                  <Box onClick={() => setBillSubTab('pdf')} sx={{ py: 0.7, px: 1.2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, borderBottom: billSubTab === 'pdf' ? `2px solid ${FI_ORANGE}` : '2px solid transparent' }}>
                    <PictureAsPdfIcon sx={{ fontSize: 12, color: billSubTab === 'pdf' ? FI_ORANGE : '#bbb' }} />
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: billSubTab === 'pdf' ? '#333' : '#999' }}>PDF Preview</Typography>
                  </Box>
                  <Box onClick={() => setBillSubTab('summary')} sx={{ py: 0.7, px: 1.2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, borderBottom: billSubTab === 'summary' ? `2px solid ${FI_BLUE}` : '2px solid transparent' }}>
                    <SummarizeIcon sx={{ fontSize: 12, color: billSubTab === 'summary' ? FI_BLUE : '#bbb' }} />
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: billSubTab === 'summary' ? '#333' : '#999' }}>Summary</Typography>
                  </Box>
                </Stack>
              ) : <Box sx={{ flex: 1 }} />}
              {(uploadedFile || processed) && (
                <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                  <Button size="small" startIcon={<Sparkle size={11} variant="single" color={BRAND} />}
                    onClick={() => { fileInputRef.current?.click(); setBillSubTab('pdf'); }}
                    sx={{ textTransform: 'none', fontSize: '0.64rem', color: BRAND, minWidth: 0, px: 1, py: 0.4 }}>
                    Re-read
                  </Button>
                  <Divider orientation="vertical" flexItem sx={{ my: 0.6 }} />
                  <Button size="small" startIcon={<DeleteSweepIcon sx={{ fontSize: 12 }} />}
                    onClick={deleteAll}
                    sx={{ textTransform: 'none', fontSize: '0.64rem', color: '#c62828', minWidth: 0, px: 1, py: 0.4 }}>
                    Delete Import
                  </Button>
                </Stack>
              )}
              <Tooltip title="Hide PDF preview entirely" arrow placement="left">
                <IconButton size="small" onClick={() => setPdfState('hidden')}
                  sx={{ mr: 0.3, color: '#888', p: 0.4,
                    '&:hover': { color: BRAND, backgroundColor: '#f5f0ff' } }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* ── PDF sub-tab ── */}
            {billSubTab === 'pdf' && (
              <>
                {pdfUrl ? (
                  <Box>
                    {/* PDF viewer with drag-over support */}
                    <Box
                      onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      sx={{ height: pdfViewHeight, backgroundColor: '#fafafa', position: 'relative' }}>
                      {uploadedFile?.type === 'application/pdf' ? (
                        <iframe src={pdfUrl} title="Invoice preview" style={{ width: '100%', height: '100%', border: 'none' }} />
                      ) : (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                          <img src={pdfUrl} alt="Invoice" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                      )}
                      {/* Drag-over overlay for re-import */}
                      {isDragging && (
                        <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(103,78,167,0.18)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)', zIndex: 10 }}>
                          <CloudUploadIcon sx={{ fontSize: 44, color: BRAND, mb: 0.8 }} />
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: BRAND }}>Drop to re-import</Typography>
                        </Box>
                      )}
                      {/* File name chips */}
                      {uploadedFile && (
                        <Box sx={{ position: 'absolute', bottom: 6, left: 8, display: 'flex', gap: 0.5, pointerEvents: 'none' }}>
                          <Chip label={uploadedFile.name} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', maxWidth: 180 }} />
                          <Chip label={`${(uploadedFile.size / 1024).toFixed(0)} KB`} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)' }} />
                        </Box>
                      )}
                    </Box>
                    {/* Vertical drag-to-resize handle */}
                    <Box
                      onMouseDown={handlePdfHeightDragStart}
                      sx={{ height: 14, cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f0ff', borderTop: `1px solid ${BRAND}22`, '&:hover': { backgroundColor: '#ece6ff' }, '&:active': { backgroundColor: '#d8cff0' } }}>
                      <Stack direction="row" spacing={0.4}>
                        {[0,1,2,3,4].map(i => <Box key={i} sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: BRAND, opacity: i === 0 || i === 4 ? 0.25 : 0.55 }} />)}
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ height: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: isDragging ? '#f5f0ff' : '#fafafa', transition: 'all 0.2s', '&:hover': { backgroundColor: '#f5f0ff' } }}>
                    <CloudUploadIcon sx={{ fontSize: 40, color: isDragging ? BRAND : '#ccc', mb: 0.8 }} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: isDragging ? BRAND : '#666' }}>Drop invoice here</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#aaa', mt: 0.3 }}>Auto-parses on upload</Typography>
                  </Box>
                )}
              </>
            )}

            {/* ── Summary sub-tab ── */}
            {billSubTab === 'summary' && processed && (
              <Box sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {parsedMeta?.supplierInvoiceNo && (
                    <Box>
                      <Typography sx={{ fontSize: '0.58rem', color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>Invoice Number</Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#333' }}>#{parsedMeta.supplierInvoiceNo}</Typography>
                    </Box>
                  )}
                  {parsedMeta?.invoiceDate && (
                    <Box>
                      <Typography sx={{ fontSize: '0.58rem', color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>Invoice Date</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#333' }}>{parsedMeta.invoiceDate}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>Supplier</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#333' }}>{supplier?.name}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>GST Treatment</Typography>
                    <Chip label={globalGst === 'ex' ? 'Ex GST' : 'Inc GST'} size="small"
                      sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, backgroundColor: globalGst === 'ex' ? '#e8f5e9' : '#fff3e0', color: globalGst === 'ex' ? '#2e7d32' : '#e65100' }} />
                  </Box>
                  <Divider />
                    <Box>
                      <Typography sx={{ fontSize: '0.58rem', color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>Line Items</Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: BRAND }}>{lineItems.length}</Typography>
                    </Box>
                  <Divider />
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>Subtotal</Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmt(subTotal)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>GST</Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmt(totalGst)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.8, backgroundColor: '#f5f0ff', borderRadius: 1, mt: 0.5 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: BRAND }}>Total</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: BRAND }}>{fmt(totalInc)}</Typography>
                    </Box>
                  </Box>
                  {demoMode && <Chip label="Demo mode" size="small" color="info" sx={{ fontSize: '0.62rem', alignSelf: 'flex-start' }} />}
                </Stack>
              </Box>
            )}
          </Paper>
          )}

        </Box>
        )}

        {/* ── Lean expand/collapse divider (shared with Statement Recon + Quote→PO) ── */}
        {!pdfHidden && (
          <CollapseDivider
            collapsed={leftCollapsed}
            onToggle={toggleLeftCollapsed}
            onDragStart={handleDragStart}
            expandLabel={uploadedFile?.name ? `Expand "${uploadedFile.name}"` : 'Expand PDF preview'}
            collapseLabel="Collapse PDF preview"
          />
        )}

        {/* ────────── RIGHT PANEL: Bill Details + Line Items ────────── */}
        <Box sx={{ flex: 1, minWidth: 0, ml: 1.5 }}>
          <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }} id="bill-print-area">

            {/* ── v1.46 — reference-driven link offer / confirmation ── */}
            {linkSuggestion && (() => {
              const m = linkSuggestion;
              const target = m.po ? m.po.id : m.jobRef ? m.jobRef : m.project!.number;
              const label = m.po
                ? `PO ${m.po.id}${m.po.reference ? ` (ref ${m.po.reference})` : ''}`
                : m.jobRef ? `job ${m.jobRef}` : `project ${m.project!.number}`;
              const verb = m.project && !m.po && !m.jobRef ? 'associate this bill with' : 'link this bill to';
              return (
                <Alert severity="info" className="no-print" sx={{ mb: 1.5, fontSize: '0.78rem',
                  '& .MuiAlert-message': { width: '100%' } }}>
                  <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      Reference <strong>"{bill?.reference}"</strong> looks like {label}. Do you want to {verb} <strong>{target}</strong>?
                    </Box>
                    <Button size="small" variant="contained" disableElevation onClick={() => applyLinkSuggestion()}
                      sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, '&:hover': { backgroundColor: '#4A0A82' } }}>
                      {m.po ? `Link to ${m.po.id}` : m.jobRef ? `Link to ${m.jobRef}` : `Associate ${m.project!.number}`}
                    </Button>
                    <Button size="small" onClick={() => applyLinkSuggestion({ always: true })}
                      sx={{ textTransform: 'none', color: BRAND, fontWeight: 700 }}>
                      Always do this
                    </Button>
                    <Button size="small" onClick={() => setLinkSuggestion(null)}
                      sx={{ textTransform: 'none', color: '#888' }}>
                      Dismiss
                    </Button>
                  </Stack>
                </Alert>
              );
            })()}
            {linkConfirmation && (
              <Alert severity="success" className="no-print" icon={<Sparkle size={14} variant="single" color="#1b7a43" />}
                onClose={() => setLinkConfirmation(null)} sx={{ mb: 1.5, fontSize: '0.78rem', fontWeight: 600 }}>
                {linkConfirmation}
              </Alert>
            )}

            {/* ── Masthead + reconcile step strip (v1.31 — PO-pattern shell) ── */}
            <Box className="no-print po-masthead-card" sx={{
              backgroundColor: '#faf8ff', border: '1px solid #e0d5f5', borderRadius: 2,
              overflow: 'hidden', mb: 1.5,
            }}>
              <Box sx={{ p: 2 }}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={toggleDetailsExpanded}
                  onKeyDown={disclosureKeyDown(toggleDetailsExpanded)}
                  sx={{ cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, py: 0.25,
                    '&:hover': { backgroundColor: '#f3edfc' } }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND,
                      textTransform: 'uppercase', letterSpacing: 1 }}>
                      Bill details
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.3}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>Edit details</Typography>
                      <ExpandMoreIcon sx={{
                        fontSize: 18, color: BRAND,
                        transform: detailsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.15s ease',
                      }} />
                    </Stack>
                  </Stack>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2a2a2a', mt: 0.3 }}>
                    {supplier?.name ?? '—'}
                  </Typography>
                  <Stack direction="row" spacing={3.5} mt={1} flexWrap="wrap" useFlexGap>
                    {[
                      ['Bill date', fmtDate(bill.date)],
                      ['Supplier inv #', parsedMeta?.supplierInvoiceNo || bill.supplierInvoiceNo || '—'],
                      ['Linked PO', bill.linkedPoId ?? '—'],
                      ['Total inc GST', `$${(bill.totalIncGst ?? 0).toFixed(2)}`],
                    ].map(([label, value]) => (
                      <Box key={label}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888',
                          textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {label}
                        </Typography>
                        <Typography sx={{
                          fontSize: '0.82rem', fontWeight: 600, mt: 0.1,
                          color: label === 'Linked PO' && bill.linkedPoId ? FI_ORANGE : 'inherit',
                          fontFamily: label === 'Linked PO' && bill.linkedPoId ? 'monospace' : 'inherit',
                        }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                    {bill.jobRef && (
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888',
                          textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Job
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: FI_ORANGE, mt: 0.1 }}>
                          {bill.jobRef}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
              {detailsExpanded && (
                <>
                  <Divider sx={{ borderColor: '#e0d5f5' }} />
                  <Box sx={{ px: BILL_MASTHEAD_EDIT_INSET, pb: BILL_MASTHEAD_EDIT_INSET, pt: 1.25 }}>
                    <BillEditForm bill={bill} />
                  </Box>
                </>
              )}
            </Box>

            <BillReconcileStepStrip currentStep={reconcileStep} nextHint={nextHint} />

            {/* ── Primary import drop zone (step = import) ───────────────── */}
            {!processing && !processed && (
              <Box className="no-print" sx={{ mb: 2 }}>
                <InfoableButton info={RECON_FOOTER_INFO.import}>
                  <Box sx={{ width: '100%' }}>
                    <AiImportBanner
                      fullWidth
                      onFile={processFile}
                      parsing={processing}
                      title="Import supplier invoice"
                      parsingLabel="Alfred is reading your invoice…"
                      accept=".pdf,image/*"
                      buttonLabel="Choose invoice"
                      subtitle={linkedPo
                        ? `Drag and drop PDF or image · Alfred extracts lines to reconcile against ${linkedPo.id}`
                        : 'Drag and drop PDF or image · Alfred extracts line items and attaches the file'}
                    />
                  </Box>
                </InfoableButton>
                {showFindExistingBill && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 0.5 }}>
                      <Divider sx={{ flex: 1 }} />
                      <Typography sx={{ fontSize: '0.65rem', color: '#bbb', px: 1 }}>or</Typography>
                      <Divider sx={{ flex: 1 }} />
                    </Box>
                    <Button fullWidth variant="outlined" size="small"
                      startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setFindBillSearch(''); setFindBillOpen(true); }}
                      sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: '#d0c5f0', color: BRAND,
                        py: 0.8, borderStyle: 'dashed',
                        '&:hover': { borderColor: BRAND, backgroundColor: '#faf8ff' } }}>
                      Find an existing bill already in the system
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* ── Processing animation ─────────────────────────────── */}
            {processing && (
              <Box sx={{ mb: 2, p: 2.5, backgroundColor: BRAND_LIGHT, borderRadius: 2, border: `1px solid ${BRAND_BORDER}` }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <Sparkle size={18} />
                  <Typography sx={{ fontWeight: 700, color: BRAND, fontSize: '0.85rem' }}>Alfred is reading your invoice…</Typography>
                  <CircularProgress size={14} sx={{ ml: 0.5 }} />
                </Stack>
                <LinearProgress variant="determinate" value={(processStep / (PROCESSING_STEPS.length - 1)) * 100}
                  sx={{ mb: 1.5, height: 5 }} />
                {PROCESSING_STEPS.slice(0, processStep + 1).map((step, i) => (
                  <Typography key={i} sx={{ fontSize: '0.7rem', color: i === processStep ? BRAND : '#888', fontWeight: i === processStep ? 700 : 400, mb: 0.2 }}>
                    {i < processStep ? '✓ ' : '→ '}{step}
                  </Typography>
                ))}
              </Box>
            )}

            {/* ── Banners ──────────────────────────────────────────── */}
            {processed && demoMode && !parseError && !aiFailure && (
              <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.72rem' }}>
                <strong>Demo mode</strong> — showing sample items. Set ANTHROPIC_API_KEY on Heroku for real parsing.
              </Alert>
            )}
            {aiFailure && (() => {
              const { code, message, docsUrl } = aiFailure;
              const titleByCode: Record<typeof code, string> = {
                INSUFFICIENT_CREDITS: 'AI parsing paused — API credits exhausted',
                RATE_LIMITED:         'AI parsing rate-limited',
                OVERLOADED:           'AI service overloaded',
                BAD_API_KEY:          'AI parsing key invalid',
                UNKNOWN:              'AI parsing unavailable',
              };
              const sev: 'info' | 'warning' = code === 'INSUFFICIENT_CREDITS' ? 'info' : 'warning';
              return (
                <Alert severity={sev} sx={{ mb: 1.5, fontSize: '0.72rem' }}
                  action={docsUrl ? (
                    <Button color="inherit" size="small" sx={{ fontSize: '0.65rem' }}
                      href={docsUrl} target="_blank" rel="noopener noreferrer">
                      Top up credits
                    </Button>
                  ) : undefined}>
                  <strong>{titleByCode[code]}</strong> — {message}
                </Alert>
              );
            })()}
            {parseError && !aiFailure && (
              <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.72rem' }}>
                <strong>Parse warning:</strong> {parseError} — using demo items.
              </Alert>
            )}


            {linkedPo ? (
              <BillReconcileSideBySide
                linkedPo={linkedPo}
                bill={bill}
                billImported={billImported}
                processing={processing}
                lineItems={lineItems}
                poLinesForRecon={poLinesForRecon}
                billMarkup={billMarkup}
                onBillMarkupChange={setBillMarkup}
                gstMode={globalGst}
                onGstModeChange={setGlobalGst}
                acceptedExtras={acceptedExtras}
                onAcceptExtra={handleAcceptExtra}
                onAcceptAllExtras={handleAcceptAllExtras}
                onUpdatePoLineField={(code, field, qty) => updatePoLineField(code, field, qty)}
                onReceiveAllMatched={handleReceiveAllMatched}
                onUpdateBillQty={(lineId, qty) => updateBillLineQty(lineId, qty)}
                supplier={supplier ?? null}
              />
            ) : (
            <BillReconciliationTable
              billImported={billImported}
              hasPOItems={hasPOItems}
              syncedPoLines={syncedPoLines}
              linkedPo={linkedPo ?? null}
              bill={bill}
              supplier={supplier ?? null}
              lineItems={lineItems}
              poLinesForRecon={poLinesForRecon}
              reconRows={reconRows}
              products={products}
              trackSupplierCodes={trackSupplierCodes}
              processed={processed}
              totalInc={totalInc}
              globalGst={globalGst}
              lineGstOverrides={lineGstOverrides}
              selectedIds={selectedIds}
              billMarkup={billMarkup}
              showSalesPrice={showSalesPrice}
              onGlobalGstChange={setGlobalGst}
              onApplyGlobalToAll={applyGlobalToAll}
              onToggleLineGst={toggleLineGst}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onClearSelection={() => setSelectedIds(new Set())}
              onDeleteSelected={deleteSelected}
              onDeleteLine={deleteLine}
              onBillMarkupChange={setBillMarkup}
              onToggleSalesPrice={() => setShowSalesPrice(v => !v)}
              salesOf={salesOf}
              onUpdatePoLineField={updatePoLineField}
              onUpdateBillLineQty={updateBillLineQty}
              onUpdatePoStatus={(poId, status) => updatePurchaseOrder(poId, { status })}
              onResolveBillLine={setResolveBillLineId}
              lookupSupplierPriceBookEntry={lookupSupplierPriceBookEntry}
              lookupSupplierCode={lookupSupplierCode}
            />
            )}

            {/* Processed but all deleted */}
            {processed && lineItems.length === 0 && !processing && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <UploadFileIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#888' }}>No line items</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#aaa', mt: 0.3 }}>
                  Drop a new invoice on the left panel to re-import
                </Typography>
              </Box>
            )}

            {/* Totals — legacy layout when bill is not linked to a PO */}
            {!linkedPo && (billImported || hasPOItems) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1, gap: 2 }}>
                {/* Left: PO totals + sync buttons */}
                <Box>
                  {linkedPo && (() => {
                    const poSubTotal = poLinesForRecon.reduce((s, l) => s + l.orderedQty * l.unitPrice * (1 - (l.discount || 0) / 100), 0);
                    const poGst = poSubTotal * 0.1;
                    const poTotal = poSubTotal + poGst;
                    return (
                      <Box sx={{ width: 240, mb: 1.2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                          <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>PO Subtotal</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{fmt(poSubTotal)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                          <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>GST</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{fmt(poGst)}</Typography>
                        </Box>
                        <Divider sx={{ my: 0.6 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, backgroundColor: '#f5f0ff', px: 1, borderRadius: 1 }}>
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: BRAND }}>PO TOTAL</Typography>
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: BRAND }}>{fmt(poTotal)}</Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
                {/* Right: Bill Totals + Markup + Save/Approve — only when invoice is imported */}
                {billImported ? (
                  <>
                  {/* Right: Bill totals + Save/Approve */}
                  <Box sx={{ width: 240 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>Bill Subtotal</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{fmt(subTotal)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>GST</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{fmt(totalGst)}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.6 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, backgroundColor: '#f5f0ff', px: 1, borderRadius: 1 }}>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: BRAND }}>BILL TOTAL</Typography>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: BRAND }}>{fmt(totalInc)}</Typography>
                    </Box>

                  </Box>
                  </>
                ) : null}
              </Box>
            )}

            {bill.notes && <Alert severity="info" sx={{ fontSize: '0.72rem', mt: 1.5 }}>{bill.notes}</Alert>}

            {/* Version lives in app Layout footer only — no mid-page prototype stamp. */}

            {/* ── Bottom action bar — doc tools scroll; lifecycle sticky below ─ */}
            <Box className="no-print" sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
              <Stack direction="row" spacing={0.8} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
                <InfoableButton info={RECON_FOOTER_INFO.preview}>
                  <Button size="small" disableElevation startIcon={<VisibilityIcon />}
                    onClick={() => navigate(`/bills/${bill.id}`)}
                    sx={BTN_GHOST_DOC}>Preview</Button>
                </InfoableButton>
                <InfoableButton info={RECON_FOOTER_INFO.pdf}>
                  <Button size="small" disableElevation startIcon={<DownloadIcon />}
                    onClick={() => window.print()} sx={BTN_GHOST_DOC}>PDF</Button>
                </InfoableButton>
                <InfoableButton info={RECON_FOOTER_INFO.reload}>
                  <Button size="small" disableElevation startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={BTN_GHOST_DOC}>Reload invoice</Button>
                </InfoableButton>
                <InfoableButton info={bill.accountingExport ? RECON_FOOTER_INFO.resend : RECON_FOOTER_INFO.exportXero}>
                  <Button size="small" disableElevation startIcon={<OpenInNewIcon />}
                    onClick={() => sendToAccounting(false)}
                    sx={BTN_GHOST_DOC}>
                    {bill.accountingExport ? `Re-send to ${accountingProduct}` : `Send to ${accountingProduct}`}
                  </Button>
                </InfoableButton>
              </Stack>
            </Box>

            {/* Embedded: the lifecycle row must not stick over the PO page's own
                sticky nav — render it as a normal (static) row instead. */}
            <Box className="no-print po-sticky-nav"
              sx={embedded
                ? { ...PO_STICKY_LIFECYCLE_SX, position: 'static', zIndex: 'auto', mx: 0, px: 0, boxShadow: 'none' }
                : PO_STICKY_LIFECYCLE_SX}>
              <InfoableButton info={RECON_FOOTER_INFO.close}>
                <Button size="small" disableElevation onClick={closeView}
                  sx={BTN_STANDARD}>{linkedPo ? `Close · ${linkedPo.id}` : 'Close'}</Button>
              </InfoableButton>
              <Box sx={{ width: 24, flexShrink: 0 }} />

              {!processed && (
                <InfoableButton info={RECON_FOOTER_INFO.import}>
                  <Button size="small" variant="contained" disableElevation
                    startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={BTN_STATUS_GREEN}>
                    Import supplier invoice
                  </Button>
                </InfoableButton>
              )}
              {/* Bill actions: Approve Bill (primary) → Sync Bill to Accounting */}
              {processed && (bill.status === 'Draft' || bill.status === 'Awaiting approval') && (
                <InfoableButton info={RECON_FOOTER_INFO.approve}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => updateBill(bill.id, { status: 'Approved' })}
                    sx={BTN_STATUS_GREEN}>
                    Approve Bill
                  </Button>
                </InfoableButton>
              )}
              {processed && (bill.status === 'Draft' || bill.status === 'Awaiting approval' || bill.status === 'Approved') && (
                <InfoableButton info={bill.accountingExport ? RECON_FOOTER_INFO.resend : RECON_FOOTER_INFO.approveSend}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => sendToAccounting(bill.status !== 'Approved')}
                    sx={bill.status === 'Approved' ? BTN_STATUS_GREEN : BTN_STANDARD}
                  >
                    {bill.accountingExport
                      ? `Re-sync Bill to ${accountingProduct}`
                      : `Sync Bill to ${accountingProduct}`}
                  </Button>
                </InfoableButton>
              )}
              {bill.status === 'Approved' && bill.accountingExport && (
                <InfoableButton info={RECON_FOOTER_INFO.markPaid}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => updateBill(bill.id, { status: 'Paid' })}
                    sx={BTN_STANDARD}>Mark as paid</Button>
                </InfoableButton>
              )}

              <Box sx={PO_STICKY_LIFECYCLE_PUSH} />
              <Box sx={PO_STICKY_LIFECYCLE_TRAIL}>
                <InfoableButton info={RECON_FOOTER_INFO.deleteBill}>
                  <Button size="small" disableElevation startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
                    onClick={() => {
                      const msg = bill.status === 'Draft'
                        ? `Delete ${bill.billNo}? This cannot be undone.`
                        : `Delete ${bill.billNo}? This bill is ${bill.status}. Deleting is permanent.`;
                      if (window.confirm(msg)) {
                        deleteBillImport(bill.id);
                        closeView();
                      }
                    }}
                    sx={BTN_GHOST_DANGER}>Delete</Button>
                </InfoableButton>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Print styling lives in src/index.css under @media print — see P3.3 v1.10.8. */}

      {/* v1.33 — PO-parity supplier code import preview (before merge/replace modal) */}
      <SupplierImportPreviewDialog
        open={!!pendingBillImport}
        lines={pendingBillImport?.items ?? []}
        resolutions={billPreviewResolutions}
        targetLabel={bill?.billNo ?? 'Bill'}
        targetKind="bill"
        existingLineCount={itemsAtParseStart.current}
        trackSupplierCodes={trackSupplierCodes}
        previewNewCount={billPreviewNewCount}
        demo={pendingBillImport?.demo}
        demoMessage={pendingBillImport?.demoMessage}
        documentType="Supplier invoice"
        supplierInvoiceNo={pendingBillImport?.supplierInvoiceNo}
        fileName={pendingBillImport?.sourceFileName ?? uploadedFile?.name}
        fileUrl={pdfUrl}
        fileType={uploadedFile?.type}
        previewCollapsed={billImportPreviewCollapsed}
        onTogglePreviewCollapsed={() => setBillImportPreviewCollapsed(c => !c)}
        onSetPreviewAction={setBillPreviewAction}
        onBatchAction={batchBillPreviewAction}
        onUseSupplierCodes={useSupplierCodesAndApplyBill}
        onAutoCreateInternalCodes={autoCreateInternalCodesAndApplyBill}
        onDiscard={discardBillImportPreview}
      />

      {/*
        FI-022 FR-3 — post-parse merge / replace / attach modal.
        Opens when a parse completes against a Bill that ALREADY has
        line items. User decides what to do; nothing is silently
        overwritten.
      */}
      <Dialog open={!!pendingParse} onClose={handleJustAttachParse} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: `2px solid ${BRAND_BORDER}` } }}>
        <DialogTitle sx={{ pb: 1, pt: 2.5 }}>
          <Stack direction="row" spacing={1.3} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: BRAND,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Sparkle size={18} color="#fff" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Case B · Bill already has items (parse complete)
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#1a1a2e', mt: 0.2 }}>
                Alfred parsed {pendingParse?.items.length ?? 0} item{(pendingParse?.items.length ?? 0) === 1 ? '' : 's'}
                {pendingParse?.sourceFileName ? ' from ' + pendingParse.sourceFileName : ''}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#555', mb: 1 }}>
            This Bill already has <strong>{lineItems.length}</strong> item{lineItems.length === 1 ? '' : 's'}.
            Merge the new items in, replace the existing ones, or just attach the file (discard the parse)?
          </Typography>
          {pendingParse && pendingParse.items.length > 0 && (
            <Box sx={{ p: 1.2, mt: 1, borderRadius: 1.5, backgroundColor: BRAND_LIGHT,
              border: `1px solid ${BRAND_BORDER}`, fontSize: '0.7rem', color: '#333' }}>
              <strong>What Alfred extracted:</strong> {pendingParse.items.length} line item{pendingParse.items.length === 1 ? '' : 's'}
              {pendingParse.supplierInvoiceNo ? ' · invoice #' + pendingParse.supplierInvoiceNo : ''}
              {pendingParse.invoiceDate ? ' · dated ' + pendingParse.invoiceDate : ''}
              {pendingParse.customerPoRef ? ' · ref ' + pendingParse.customerPoRef : ''}
              {supplier && (
                <Stack spacing={0.4} sx={{ mt: 1 }}>
                  {pendingParse.items.slice(0, 6).map((item, i) => (
                    <Stack key={i} direction="row" spacing={0.6} alignItems="center">
                      <ImportResolutionBadge state={classifyImportLine({
                        supplierCode: item.supplierCode,
                        description: item.description,
                        supplierName: supplier.name,
                        products,
                        priceBookEntry: bill ? lookupSupplierPriceBookEntry(bill.supplierId, item.supplierCode) : undefined,
                        remembered: bill ? lookupSupplierCode(bill.supplierId, item.supplierCode) : undefined,
                      })} />
                      <Typography sx={{ fontSize: '0.66rem', fontFamily: 'monospace' }}>{item.supplierCode || '—'}</Typography>
                      <Typography sx={{ fontSize: '0.66rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {item.description}
                      </Typography>
                    </Stack>
                  ))}
                  {pendingParse.items.length > 6 && (
                    <Typography sx={{ fontSize: '0.62rem', color: '#888' }}>+{pendingParse.items.length - 6} more lines</Typography>
                  )}
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleJustAttachParse}
            sx={{ color: '#666', textTransform: 'none', fontSize: '0.8rem' }}>
            Just attach file — discard the parse
          </Button>
          <Box sx={{ flex: 1 }} />
          {linkedPo && (
            <Button onClick={handleNewBillParse} variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              sx={{ borderColor: '#0a6e45', color: '#0a6e45', textTransform: 'none', fontSize: '0.78rem',
                '&:hover': { backgroundColor: '#e7f6ef', borderColor: '#0a6e45' } }}>
              New bill on {linkedPo.id} — keep these {pendingParse?.items.length ?? 0} separate
            </Button>
          )}
          <Button onClick={handleReplaceParse} variant="outlined"
            sx={{ borderColor: BRAND, color: BRAND, textTransform: 'none', fontSize: '0.78rem',
              '&:hover': { backgroundColor: BRAND_LIGHT, borderColor: BRAND } }}>
            Replace — wipe the existing {lineItems.length} and use these {pendingParse?.items.length ?? 0}
          </Button>
          <Button onClick={handleMergeParse} variant="contained" startIcon={<Sparkle size={12} color="#fff" />}
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontSize: '0.78rem', fontWeight: 800,
              '&:hover': { backgroundColor: '#4A0A82' } }}>
            Merge — add these {pendingParse?.items.length ?? 0} to the existing {lineItems.length}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Sync Mismatch Dialog (Stage 1 — P1.1 line-by-line, P1.2 price tags, P1.3 clear buttons) ── */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: `2px solid ${FI_ORANGE}44` } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberIcon sx={{ color: FI_ORANGE, fontSize: 26 }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#333' }}>Review before syncing to {bill.linkedPoId}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>Select which items to sync. Unchecked items will be skipped.</Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {syncAnalysis && (() => {
            const toggleChecked = (key: string) => setSyncChecked(prev => {
              const n = new Set(prev);
              if (n.has(key)) n.delete(key); else n.add(key);
              return n;
            });

            // P1.2: price flag badge
            const PriceTag = ({ flag, pct }: { flag: SyncMatchedItem['priceFlag']; pct: number }) => {
              if (flag === 'ok') return null;
              const sign = pct > 0 ? '+' : '';
              const color = flag === 'minor' ? '#0277bd' : flag === 'moderate' ? FI_ORANGE : '#c62828';
              const bg = flag === 'minor' ? '#e3f2fd' : flag === 'moderate' ? '#fff3e0' : '#fce4ec';
              return (
                <Chip size="small"
                  label={`${sign}${pct.toFixed(1)}% price`}
                  sx={{ fontSize: '0.6rem', height: 18, backgroundColor: bg, color, fontWeight: 700, ml: 0.5 }} />
              );
            };

            // Summary chips
            const matchedWithDiff = syncAnalysis.matched.filter(m => !m.qtyMatch || m.priceFlag !== 'ok');
            const exactMatches    = syncAnalysis.matched.filter(m => m.qtyMatch && m.priceFlag === 'ok');
            const priceVarCount = syncAnalysis.matched.filter(m => m.priceFlag !== 'ok').length;

            return (
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 0.6 }}>
                  {syncAnalysis.matched.filter(m => m.qtyMatch && m.priceFlag === 'ok').length > 0 && (
                    <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                      label={`${syncAnalysis.matched.filter(m => m.qtyMatch && m.priceFlag === 'ok').length} exact match`}
                      sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                  {matchedWithDiff.length > 0 && (
                    <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: 12 }} />}
                      label={`${matchedWithDiff.length} with differences`}
                      sx={{ backgroundColor: '#fff3e0', color: FI_ORANGE, fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                  {priceVarCount > 0 && (
                    <Chip size="small"
                      label={`${priceVarCount} price variance`}
                      sx={{ backgroundColor: '#fce4ec', color: '#c62828', fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                  {syncAnalysis.billOnly.length > 0 && (
                    <Chip size="small"
                      label={`${syncAnalysis.billOnly.length} not on PO`}
                      sx={{ backgroundColor: '#fce4ec', color: '#c62828', fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                  {syncAnalysis.priceMatches.length > 0 && (
                    <Chip size="small"
                      label={`${syncAnalysis.priceMatches.length} price match`}
                      sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                  {syncAnalysis.poOnly.length > 0 && (
                    <Chip size="small"
                      label={`${syncAnalysis.poOnly.length} PO items not in bill`}
                      sx={{ backgroundColor: '#e3f2fd', color: '#0277bd', fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                </Stack>

                {/* ── Matched items — same code, exact (ready to receipt) ── */}
                {exactMatches.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', mb: 0.6 }}>
                      Matched — same code, ready to receipt
                    </Typography>
                    <Box sx={{ border: '1px solid #e8f5e9', borderRadius: 1.5, overflow: 'hidden' }}>
                      {exactMatches.map(m => {
                        const key = `matched-${m.poLine.inventoryCode}`;
                        const checked = syncChecked.has(key);
                        return (
                          <Box key={key}
                            onClick={() => toggleChecked(key)}
                            sx={{ px: 1.5, py: 0.9, borderBottom: '1px solid #f1f8e9', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                              backgroundColor: checked ? '#f1f8e9' : '#fafafa',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { backgroundColor: checked ? '#e8f5e9' : '#f0f0f0' } }}>
                            <Checkbox size="small" checked={checked} sx={{ p: 0, flexShrink: 0 }}
                              onChange={e => { e.stopPropagation(); toggleChecked(key); }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: BRAND }}>{m.poLine.inventoryCode}</Typography>
                                <Chip size="small" label="Same code"
                                  sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                                <Chip size="small" label="Mark received"
                                  sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                              </Stack>
                              <Typography sx={{ fontSize: '0.62rem', color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.poLine.description}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#2e7d32' }}>{fmt(m.billLine.unitPrice)}</Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>{m.billLine.qty} qty</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* ── Matched items with qty or price differences ── */}
                {matchedWithDiff.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', mb: 0.6 }}>
                      Matched items with differences
                    </Typography>
                    <Box sx={{ border: '1px solid #eee', borderRadius: 1.5, overflow: 'hidden' }}>
                      {matchedWithDiff.map(m => {
                        const key = `matched-${m.poLine.inventoryCode}`;
                        const checked = syncChecked.has(key);
                        return (
                          <Box key={key}
                            onClick={() => toggleChecked(key)}
                            sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                              backgroundColor: checked ? '#fffde7' : '#fafafa',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { backgroundColor: checked ? '#fff9c4' : '#f0f0f0' } }}>
                            <Checkbox size="small" checked={checked} sx={{ p: 0, flexShrink: 0 }}
                              onChange={e => { e.stopPropagation(); toggleChecked(key); }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: BRAND }}>{m.poLine.inventoryCode}</Typography>
                                {!m.qtyMatch && (
                                  <Chip size="small" label={`${m.poLine.orderedQty} → ${m.billLine.qty} qty`}
                                    sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#fff3e0', color: FI_ORANGE, fontWeight: 700 }} />
                                )}
                                <PriceTag flag={m.priceFlag} pct={m.priceVariancePct} />
                              </Stack>
                              <Typography sx={{ fontSize: '0.62rem', color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.poLine.description}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.62rem', color: '#999' }}>PO: {fmt(m.poLine.unitPrice)}</Typography>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: m.priceFlag !== 'ok' ? '#c62828' : '#333' }}>Bill: {fmt(m.billLine.unitPrice)}</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* ── Bill-only items (not on PO) ── */}
                {syncAnalysis.billOnly.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', mb: 0.6 }}>
                      Bill items not on PO
                    </Typography>
                    <Box sx={{ border: '1px solid #eee', borderRadius: 1.5, overflow: 'hidden' }}>
                      {syncAnalysis.billOnly.map(bl => {
                        const key = `bill-${bl.id}`;
                        const checked = syncChecked.has(key);
                        return (
                          <Box key={key}
                            onClick={() => toggleChecked(key)}
                            sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                              backgroundColor: checked ? '#fce4ec' : '#fafafa',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { backgroundColor: checked ? '#f8bbd0' : '#f0f0f0' } }}>
                            <Checkbox size="small" checked={checked} sx={{ p: 0, flexShrink: 0 }}
                              onChange={e => { e.stopPropagation(); toggleChecked(key); }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#c62828' }}>{bl.supplierCode}</Typography>
                                <Chip size="small" label="Add to PO"
                                  sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#fce4ec', color: '#c62828', fontWeight: 700 }} />
                              </Stack>
                              <Typography sx={{ fontSize: '0.62rem', color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {bl.description}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.62rem', color: '#777' }}>{bl.qty} qty</Typography>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#333' }}>{fmt(bl.unitPrice)}</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* ── Price-match items (same price, different code) ── */}
                {syncAnalysis.priceMatches.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', mb: 0.6 }}>
                      Possible matches — same price, different code
                    </Typography>
                    <Box sx={{ border: '1px solid #fff3e0', borderRadius: 1.5, overflow: 'hidden' }}>
                      {syncAnalysis.priceMatches.map(pm => {
                        const key = `price-${pm.billLine.id}-${pm.poLine.inventoryCode}`;
                        const checked = syncChecked.has(key);
                        return (
                          <Box key={key}
                            onClick={() => toggleChecked(key)}
                            sx={{ px: 1.5, py: 0.9, borderBottom: '1px solid #fff8e1', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                              backgroundColor: checked ? '#fff8e1' : '#fffde7',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { backgroundColor: checked ? '#fff3cd' : '#fff9c4' } }}>
                            <Checkbox size="small" checked={checked} sx={{ p: 0, flexShrink: 0 }}
                              onChange={e => { e.stopPropagation(); toggleChecked(key); }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* Codes: bill → PO */}
                              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#e65100' }}>{pm.billLine.supplierCode}</Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>→</Typography>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: BRAND }}>{pm.poLine.inventoryCode}</Typography>
                                <Chip size="small" label="Same price"
                                  sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                                <Chip size="small" label="Mark received only"
                                  sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                              </Stack>
                              <Stack direction="row" spacing={1} sx={{ mt: 0.2 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>Bill:</Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: '#777', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pm.billLine.description}</Typography>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>PO:</Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: '#777', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pm.poLine.description}</Typography>
                              </Stack>
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#2e7d32' }}>{fmt(pm.billLine.unitPrice)}</Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>{pm.billLine.qty} qty</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    <Typography sx={{ fontSize: '0.6rem', color: '#aaa', mt: 0.5, fontStyle: 'italic' }}>
                      If accepted: PO item marked received. Code stays as-is — no changes to price or ordered qty.
                    </Typography>
                  </Box>
                )}

                {/* ── PO-only items (not in bill — informational) ── */}
                {syncAnalysis.poOnly.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', mb: 0.6 }}>
                      PO items not in bill — kept as outstanding
                    </Typography>
                    <Box sx={{ border: '1px solid #e3f2fd', borderRadius: 1.5, overflow: 'hidden' }}>
                      {syncAnalysis.poOnly.map(pl => (
                        <Box key={pl.inventoryCode}
                          sx={{ px: 1.5, py: 0.7, borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: 1, opacity: 0.65,
                            backgroundColor: '#f8fbff', '&:last-child': { borderBottom: 'none' } }}>
                          <Box sx={{ width: 20, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#0277bd' }}>{pl.inventoryCode}</Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.description}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#0277bd', fontWeight: 600, flexShrink: 0 }}>
                            {pl.orderedQty} ordered · outstanding
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* ── Sync options: Receipt All + Update ordered qty ── */}
                <Box sx={{ pt: 1.5, mt: 0.5, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {/* Receipt All items — master toggle, defaulted ON */}
                  <Box sx={{ border: '1px solid', borderColor: syncReceiptAll ? '#c8e6c9' : '#e0e0e0',
                    backgroundColor: syncReceiptAll ? '#f1f8e9' : '#fafafa', borderRadius: 1.5, px: 1.5, py: 1.25, transition: 'all .15s' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={syncReceiptAll}
                          onChange={e => setSyncReceiptAll(e.target.checked)}
                          sx={{ p: 0, mr: 1, color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' },
                            '& .MuiSvgIcon-root': { fontSize: 26 } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#1b5e20' }}>Receipt all items</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#558b2f' }}>
                            On: matched lines are marked received when synced · Off: lines still sync to the PO but received qty stays unchanged
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.2 } }}
                    />
                  </Box>

                  {/* P0.1: Update ordered quantities toggle */}
                  <Box sx={{ border: '1px solid', borderColor: syncUpdateOrdered ? '#bbdefb' : '#e0e0e0',
                    backgroundColor: syncUpdateOrdered ? '#e3f2fd' : '#fafafa', borderRadius: 1.5, px: 1.5, py: 1.25, transition: 'all .15s' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={syncUpdateOrdered}
                          onChange={e => setSyncUpdateOrdered(e.target.checked)}
                          sx={{ p: 0, mr: 1, color: '#0277bd', '&.Mui-checked': { color: '#0277bd' },
                            '& .MuiSvgIcon-root': { fontSize: 26 } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#01579b' }}>Also update original ordered quantities</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#0277bd' }}>
                            Off: only received qty is updated (ordered stays as approved) ·
                            On: ordered qty is rebased to bill qty
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.2 } }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>

        {/* P1.3: Cancel / Sync Selected */}
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setSyncDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#666', borderColor: '#ddd', '&:hover': { backgroundColor: '#f5f5f5' } }}>
            Cancel
          </Button>
          <Button variant="contained"
            disabled={syncChecked.size === 0}
            onClick={() => syncAnalysis && performSync(syncAnalysis, syncChecked, syncUpdateOrdered)}
            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: '#4a3580' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#aaa' } }}>
            Sync Selected ({syncChecked.size} item{syncChecked.size !== 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Find Existing Bill Dialog ────────────────────────────────── */}
      <Dialog open={findBillOpen} onClose={() => setFindBillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: BRAND }} />
            <Box>
              <Typography fontWeight={800}>Find Existing Bill</Typography>
              <Typography variant="caption" color="text.secondary">
                Navigate to a bill already in the system — or link it to {linkedPo?.id ?? 'this PO'}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" placeholder="Search by bill #, supplier, invoice #, PO, job…"
            value={findBillSearch} onChange={e => setFindBillSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 2 }}
          />
          {candidateBills.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#bbb' }}>
                {findBillSearch ? 'No bills match your search' : 'No other bills in the system yet'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.5}>
              {candidateBills.slice(0, 20).map(b => {
                const sup = suppliers.find(s => s.id === b.supplierId);
                const bPo = b.linkedPoId ? purchaseOrders.find(p => p.id === b.linkedPoId) : null;
                return (
                  <Box key={b.id} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2,
                    border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa',
                    '&:hover': { backgroundColor: '#f5f0ff', borderColor: BRAND + '40' },
                  }}>
                    <Avatar sx={{ width: 28, height: 28, backgroundColor: BRAND, fontSize: '0.58rem', fontWeight: 800 }}>
                      {sup?.code?.slice(0, 3) ?? '?'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND }}>{b.billNo}</Typography>
                        <StatusChip status={b.status} dense />
                        {b.processed && (
                          <StatusChip status="Imported" tone="success" dense />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: '0.68rem', color: '#555' }} noWrap>
                        {sup?.name ?? '—'}{b.supplierInvoiceNo ? ` · #${b.supplierInvoiceNo}` : ''}{b.date ? ` · ${b.date}` : ''}
                      </Typography>
                      {bPo && (
                        <Typography sx={{ fontSize: '0.62rem', color: '#888' }}>
                          Linked to {bPo.id} · {bPo.jobRef ?? ''}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} flexShrink={0}>
                      {linkedPo && b.linkedPoId !== linkedPo.id && (
                        <Tooltip title={`Link this bill to ${linkedPo.id}`}>
                          <Button size="small" variant="outlined"
                            startIcon={<LinkIcon sx={{ fontSize: 11 }} />}
                            onClick={() => { updateBill(b.id, { linkedPoId: linkedPo.id }); setFindBillOpen(false); if (embedded) goToBill(b.id); else navigate(`/bills/${b.id}`); }}
                            sx={{ textTransform: 'none', fontSize: '0.65rem', color: BRAND, borderColor: BRAND, py: 0.3, px: 0.8, minWidth: 0 }}>
                            Link & Open
                          </Button>
                        </Tooltip>
                      )}
                      <Button size="small" variant="contained"
                        onClick={() => { setFindBillOpen(false); if (embedded && b.linkedPoId === linkedPo?.id) goToBill(b.id); else navigate(`/bills/${b.id}`); }}
                        sx={{ textTransform: 'none', fontSize: '0.65rem', backgroundColor: BRAND, py: 0.3, px: 0.8, minWidth: 0 }}>
                        Open
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFindBillOpen(false)} sx={{ textTransform: 'none', color: '#666' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Unallocated-code resolution (v1.17) — bill side */}
      {resolveBillLineId !== null && (() => {
        const bl = lineItems.find(l => l.id === resolveBillLineId);
        if (!bl || !bill) return null;
        return (
          <ResolveCodeDialog
            open
            supplierId={bill.supplierId}
            supplierName={supplier?.name ?? 'Supplier'}
            line={{ supplierCode: bl.supplierCode, description: bl.description, unitPrice: bl.unitPrice }}
            products={products}
            onClose={() => setResolveBillLineId(null)}
            onResolved={(internalCode, remember) => handleResolveBillLine(bl.id, internalCode, remember)}
            onCreateProduct={handleCreateProductFromBill}
            onAutoCreateAndMatch={() => handleAutoCreateBillLine(bl.id)}
            onMarkService={() => handleMarkBillLineService(bl.id)}
          />
        );
      })()}
    </Box>
  );
};

export { BillReconcileView as BillDetailPage };
export default BillReconcileView;
